var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var __privateWrapper = (obj, member, setter, getter) => ({
  set _(value) {
    __privateSet(obj, member, value, setter);
  },
  get _() {
    return __privateGet(obj, member, getter);
  }
});

// 08-themes/size-diagnosis-src/body-choice/body-choice-canonical.js
function recommendSize(rules, height, weight) {
  if (height < 130 || height > 200) return { status: "INVALID_INPUT", reason: "INVALID_HEIGHT" };
  if (weight < 30 || weight > 120) return { status: "INVALID_INPUT", reason: "INVALID_WEIGHT" };
  const sizes = rules.sizes || [];
  if (!sizes.length) return { status: "NO_DATA" };
  const matching = sizes.filter(
    (s) => height >= s.height_range.min && height <= s.height_range.max && weight >= s.weight_range.min && weight <= s.weight_range.max
  );
  if (matching.length === 0) {
    const minSize = sizes[0];
    const maxSize = sizes[sizes.length - 1];
    const hBelow = height < minSize.height_range.min;
    const hAbove = height > maxSize.height_range.max;
    const wBelow = weight < minSize.weight_range.min;
    const wAbove = weight > maxSize.weight_range.max;
    let reason;
    if (hBelow && wBelow) {
      reason = "BOTH_BELOW";
    } else if (hAbove && wAbove) {
      reason = "BOTH_ABOVE";
    } else if (hBelow && wAbove) {
      reason = "HEIGHT_LOW_WEIGHT_HIGH";
    } else if (hAbove && wBelow) {
      reason = "HEIGHT_HIGH_WEIGHT_LOW";
    } else {
      reason = "ASYMMETRIC";
    }
    const nearest = reason === "BOTH_BELOW" ? minSize : reason === "BOTH_ABOVE" ? maxSize : findClosestSize(height, weight, sizes);
    return { status: "OUT_OF_RANGE", reason, nearest, heightOver: hAbove, heightUnder: hBelow };
  }
  if (matching.length === 1) {
    return {
      status: "SINGLE_MATCH",
      recommended: matching[0],
      others: sizes.filter((s) => s.label !== matching[0].label)
    };
  }
  const policy = rules.boundary_policy || "round_up";
  let recommended, alternative;
  if (policy === "round_up") {
    recommended = matching[matching.length - 1];
    alternative = matching[0];
  } else if (policy === "round_down") {
    recommended = matching[0];
    alternative = matching[matching.length - 1];
  } else {
    return {
      status: "MULTIPLE_MATCH_ASK",
      candidates: matching,
      others: sizes.filter((s) => !matching.some((m) => m.label === s.label))
    };
  }
  return {
    status: "BOUNDARY_ROUND",
    recommended,
    alternative,
    others: sizes.filter((s) => s.label !== recommended.label && s.label !== alternative.label)
  };
}
function findClosestSize(height, weight, sizes) {
  let pool = sizes.filter((s) => height <= s.height_range.max);
  if (!pool.length) pool = [sizes[sizes.length - 1]];
  let minDist = Infinity, closest = null;
  for (const s of pool) {
    const hMid = (s.height_range.min + s.height_range.max) / 2;
    const wMid = (s.weight_range.min + s.weight_range.max) / 2;
    const dist = Math.abs(weight - wMid) * 1 + Math.abs(height - hMid) * 0.15;
    if (dist < minDist) {
      minDist = dist;
      closest = s;
    }
  }
  return closest;
}
function compareCupSize(a, b) {
  const pa = /^(\d+)([A-Z]*)$/.exec(String(a));
  const pb = /^(\d+)([A-Z]*)$/.exec(String(b));
  if (!pa || !pb) return String(a).localeCompare(String(b));
  const band = Number(pa[1]) - Number(pb[1]);
  if (band !== 0) return band;
  if (pa[2].length !== pb[2].length) return pa[2].length - pb[2].length;
  return pa[2] < pb[2] ? -1 : pa[2] > pb[2] ? 1 : 0;
}
function buildCupIndex(cupMap) {
  const reverse = {};
  const options = [];
  if (!cupMap || typeof cupMap !== "object" || Array.isArray(cupMap)) return { reverse, options };
  for (const label of Object.keys(cupMap)) {
    const list = cupMap[label];
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      const v = String(raw == null ? "" : raw).trim().toUpperCase();
      if (!v || Object.prototype.hasOwnProperty.call(reverse, v)) continue;
      reverse[v] = label;
      options.push(v);
    }
  }
  options.sort(compareCupSize);
  return { reverse, options };
}
function applyCupAdjustment(rules, result, cupReverse, cupSize) {
  if (!result || !cupSize || !cupReverse) return result;
  const cupLabel = cupReverse[String(cupSize).trim().toUpperCase()];
  if (!cupLabel) return result;
  const sizes = rules && rules.sizes || [];
  const order = sizes.map((s) => s.label);
  const cupIdx = order.indexOf(cupLabel);
  if (cupIdx < 0) return result;
  if (result.status === "MULTIPLE_MATCH_ASK") {
    const cands = result.candidates || [];
    if (!cands.length) return result;
    const keep = cands.filter((c) => order.indexOf(c.label) >= cupIdx);
    if (keep.length === cands.length) return result;
    if (keep.length >= 2) {
      return Object.assign({}, result, { candidates: keep, cupBase: cands[0].label });
    }
    const picked = keep[0] || sizes[cupIdx];
    if (!picked) return result;
    return Object.assign({}, result, {
      status: "SINGLE_MATCH",
      recommended: picked,
      candidates: [],
      others: sizes.filter((s) => s.label !== picked.label),
      adjusted: "up",
      cupBase: cands[0].label
    });
  }
  const baseKey = result.recommended ? "recommended" : result.nearest ? "nearest" : null;
  if (!baseKey) return result;
  const baseIdx = order.indexOf(result[baseKey].label);
  if (baseIdx < 0 || cupIdx <= baseIdx) return result;
  const target = sizes[cupIdx];
  if (!target) return result;
  const patch = { others: sizes.filter((s) => s.label !== target.label), adjusted: "up", cupBase: result[baseKey].label };
  patch[baseKey] = target;
  return Object.assign({}, result, patch);
}
var OVERRIDE_WEIGHT_MIN = 30;
var OVERRIDE_WEIGHT_MAX = 120;
function _labelOf(result) {
  if (!result) return null;
  if (result.status === "SINGLE_MATCH" || result.status === "BOUNDARY_ROUND") return result.recommended ? result.recommended.label : null;
  if (result.status === "OUT_OF_RANGE" && result.reason !== "BOTH_ABOVE") return result.nearest ? result.nearest.label : null;
  return null;
}
function _validCell(c) {
  const intPair = (a) => Array.isArray(a) && a.length === 2 && Number.isInteger(a[0]) && Number.isInteger(a[1]) && a[0] <= a[1];
  return !!c && typeof c === "object" && intPair(c.h) && intPair(c.w) && typeof c.size === "string" && c.size.length > 0;
}
function _overrideCell(cells, h, w) {
  for (const c of cells) {
    if (h >= c.h[0] && h <= c.h[1] && w >= c.w[0] && w <= c.w[1]) return c;
  }
  return null;
}
function applyEvidenceOverride(rules, result, overrides, height, weight) {
  if (!result || !rules || !overrides || !Number.isFinite(height) || !Number.isFinite(weight)) return result;
  const cats = Array.isArray(overrides.categories) ? overrides.categories : [];
  if (!cats.includes(rules.category)) return result;
  const cells = Array.isArray(overrides.cells) ? overrides.cells.filter(_validCell) : [];
  if (!cells.length) return result;
  const cur = _labelOf(result);
  if (!cur) return result;
  const sizes = rules.sizes || [];
  const order = sizes.map((s) => s.label);
  const ci = order.indexOf(cur);
  if (ci < 0) return result;
  let down = null, up = null;
  const scan = (x) => {
    const cell = _overrideCell(cells, height, x);
    if (!cell) return;
    const cx = order.indexOf(_labelOf(recommendSize(rules, height, x)));
    const t = order.indexOf(cell.size);
    if (cx < 0 || t < 0 || Math.abs(t - cx) !== 1) return;
    if (t < cx && x >= weight) down = down == null ? t : Math.min(down, t);
    if (t > cx && x <= weight) up = up == null ? t : Math.max(up, t);
  };
  scan(weight);
  for (let x = OVERRIDE_WEIGHT_MIN; x <= OVERRIDE_WEIGHT_MAX; x++) if (x !== weight) scan(x);
  let f = ci;
  if (down != null) f = Math.min(f, down);
  if (up != null) f = Math.max(f, up);
  if (f === ci || !sizes[f]) return result;
  const target = sizes[f];
  return {
    status: "OUT_OF_RANGE",
    // ⑥
    reason: "EVIDENCE",
    nearest: target,
    heightOver: !!result.heightOver,
    heightUnder: !!result.heightUnder,
    others: sizes.filter((s) => s.label !== target.label),
    overriddenFrom: cur
  };
}
var SECOND_ADJ_MAX_WEIGHT_OFFSET = 6;
function pickSecondSize(rules, recLabel, height, weight, opts = {}) {
  const sizes = rules && rules.sizes || [];
  const order = sizes.map((s) => s.label);
  const i = order.indexOf(recLabel);
  if (i < 0 || sizes.length < 2) return null;
  const hasHW = Number.isFinite(height) && Number.isFinite(weight);
  const floorIdx = Number.isInteger(opts.floorIdx) ? opts.floorIdx : -1;
  const off = (v, r) => {
    if (!r || typeof r.min !== "number" || typeof r.max !== "number") return 0;
    if (v < r.min) return r.min - v;
    if (v > r.max) return v - r.max;
    return 0;
  };
  const passes = (j, adjacent) => {
    if (j < 0 || j >= sizes.length || j === i) return false;
    if (floorIdx >= 0 && j < floorIdx) return false;
    const s = sizes[j];
    if (j < i && hasHW && s.height_range && typeof s.height_range.max === "number" && height > s.height_range.max) return false;
    if (adjacent && hasHW) {
      const r = sizes[i].weight_range || {};
      const mid = (Number(r.min) + Number(r.max)) / 2;
      if (j < i && i === sizes.length - 1 && Number.isFinite(mid) && weight >= mid) return false;
      if (off(weight, s.weight_range) > SECOND_ADJ_MAX_WEIGHT_OFFSET) return false;
    }
    return true;
  };
  for (const lab of [opts.overriddenFrom, opts.alternativeLabel]) {
    if (!lab) continue;
    const j = order.indexOf(lab);
    if (passes(j, false)) return sizes[j];
  }
  if (!hasHW) return null;
  for (const j of [i + 1, i - 1]) {
    if (passes(j, true)) return sizes[j];
  }
  return null;
}
function compareSizeMeasurements(rules, a, b) {
  if (!Array.isArray(rules?.measurement_dims) || !a?.measurements || !b?.measurements) return [];
  const sourceValue = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
    if (typeof value !== "string") return null;
    const text2 = value.trim();
    return text2 && !["-", "—", "–"].includes(text2) ? text2 : null;
  };
  return rules.measurement_dims.filter((dim) => typeof dim === "string").flatMap((dim) => {
    if (!Object.prototype.hasOwnProperty.call(a.measurements, dim) && !Object.prototype.hasOwnProperty.call(b.measurements, dim)) return [];
    const av = sourceValue(a.measurements[dim]);
    const bv = sourceValue(b.measurements[dim]);
    return av !== null || bv !== null ? [{ dim, a: av ?? "—", b: bv ?? "—" }] : [];
  });
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-engine.js
var SCHEMA = "ayunel-body-choice-catalog-v1";
var own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
var record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
var finite = (value) => typeof value === "number" && Number.isFinite(value);
var text = (value, max = 256) => typeof value === "string" && value.length > 0 && value.length <= max && value.trim() === value && !/[\u0000-\u001f\u007f]/.test(value);
var id = (value) => typeof value === "string" && /^[1-9]\d{0,19}$/.test(value);
var date = (value) => typeof value === "string" && /^\d{4}-\d\d-\d\dT/.test(value) && Number.isFinite(Date.parse(value));
var missing = (value) => value === void 0 || value === null;
var PARTS = ["shoulder", "bust", "abdomen", "hip"];
var PART_LABELS = { shoulder: "肩幅", bust: "バスト", abdomen: "お腹まわり", hip: "ヒップ" };
var CHOICES = {
  shoulder: ["狭め", "中間", "広め"],
  bust: ["小さめ", "中間", "大きめ"],
  abdomen: ["平らに近い", "少し丸み", "丸みがある"],
  hip: ["幅が狭め", "中間", "幅が広め"]
};
var DIMENSIONS = {
  shoulder: /肩|shoulder/i,
  bust: /バスト|胸|身幅|bust|chest/i,
  abdomen: /ウエスト|腰囲|腹|waist/i,
  hip: /ヒップ|臀|尻|hip/i
};
function safeTree(root) {
  let count = 0;
  const ancestors = /* @__PURE__ */ new Set();
  const visit = (value, depth) => {
    if (++count > 2e5 || depth > 20) return false;
    if (value === null || ["undefined", "string", "boolean"].includes(typeof value)) return true;
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value !== "object" || ancestors.has(value)) return false;
    const proto = Object.getPrototypeOf(value);
    if (Array.isArray(value) ? proto !== Array.prototype : proto !== Object.prototype && proto !== null) return false;
    const descriptors = Object.getOwnPropertyDescriptors(value);
    if (Object.getOwnPropertySymbols(value).length) return false;
    ancestors.add(value);
    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (["__proto__", "prototype", "constructor"].includes(key) || !own(descriptor, "value") || !visit(descriptor.value, depth + 1)) return false;
    }
    ancestors.delete(value);
    return true;
  };
  try {
    return visit(root, 0);
  } catch {
    return false;
  }
}
function validUrl(url, handle) {
  if (!text(url, 512)) return false;
  try {
    const parsed = new URL(url, "https://ayunel.com");
    return parsed.protocol === "https:" && parsed.hostname === "ayunel.com" && parsed.port === "" && !parsed.username && !parsed.password && parsed.pathname === `/products/${handle}` && !parsed.search && !parsed.hash && (url.startsWith("https://ayunel.com/") || url.startsWith("/products/"));
  } catch {
    return false;
  }
}
function range(value, minimum, maximum) {
  return record(value) && finite(value.min) && finite(value.max) && value.min >= minimum && value.max <= maximum && value.min <= value.max;
}
function measurementValue(value) {
  return finite(value) || typeof value === "string" && value.length <= 128 && !/[\u0000-\u001f\u007f]/.test(value);
}
function validRules(rules) {
  if (!record(rules) || !Array.isArray(rules.sizes) || !rules.sizes.length || rules.sizes.length > 30 || !text(rules.category, 64) || ![void 0, "round_up", "round_down", "ask"].includes(rules.boundary_policy) || !missing(rules.unit) && (!record(rules.unit) || rules.unit.height !== "cm" || rules.unit.weight !== "kg" || !missing(rules.unit.measurement) && rules.unit.measurement !== "cm") || !missing(rules.measurement_dims) && (!Array.isArray(rules.measurement_dims) || rules.measurement_dims.length > 30 || !rules.measurement_dims.every((dim) => text(dim, 128)))) return false;
  const seen = /* @__PURE__ */ new Set();
  let previous = null;
  for (const size of rules.sizes) {
    if (!record(size) || !text(size.label, 64) || seen.has(size.label) || !range(size.height_range, 1, 300) || !range(size.weight_range, 1, 300) || !missing(size.measurements) && (!record(size.measurements) || Object.entries(size.measurements).some(([key, value]) => !text(key, 128) || !measurementValue(value)))) return false;
    if (previous && ["height_range", "weight_range"].some((key) => size[key].min < previous[key].min || size[key].max < previous[key].max)) return false;
    seen.add(size.label);
    previous = size;
  }
  return true;
}
function validTable(table) {
  return record(table) && Array.isArray(table.headers) && table.headers.length > 0 && table.headers.length <= 30 && table.headers.every((value) => text(value, 128)) && Array.isArray(table.rows) && table.rows.length <= 60 && table.rows.every((row) => record(row) && text(row.label, 64) && Array.isArray(row.values) && row.values.length === table.headers.length && row.values.every((value) => typeof value === "string" && value.length <= 256 && !/[\u0000-\u001f\u007f]/.test(value)));
}
function validCup(cup, rules) {
  if (!record(cup)) return false;
  const labels = new Set((rules?.sizes || []).map((size) => size.label));
  const seen = /* @__PURE__ */ new Set();
  return Object.entries(cup).every(([label, values2]) => text(label, 64) && (!rules || labels.has(label)) && Array.isArray(values2) && values2.length <= 150 && values2.every((value) => {
    if (!text(value, 16) || !/^\d{2,3}[A-Z]{1,3}$/.test(value) || seen.has(value)) return false;
    seen.add(value);
    return true;
  }));
}
function validOverrides(overrides) {
  return record(overrides) && Array.isArray(overrides.categories) && overrides.categories.every((value) => text(value, 64)) && Array.isArray(overrides.cells) && overrides.cells.length <= 500 && overrides.cells.every((cell) => record(cell) && ["h", "w"].every((key) => Array.isArray(cell[key]) && cell[key].length === 2 && cell[key].every(Number.isInteger) && cell[key][0] <= cell[key][1]) && text(cell.size, 64));
}
function projectProduct(product, warnings) {
  const issue = (code) => warnings.push({ code, productId: product.id });
  let rules = missing(product.rules) ? null : product.rules;
  if (rules && !validRules(rules)) {
    issue("RULES_UNAVAILABLE");
    rules = null;
  }
  let sizeTable = missing(product.sizeTable) ? null : product.sizeTable;
  if (sizeTable && !validTable(sizeTable)) {
    issue("SIZE_TABLE_UNAVAILABLE");
    sizeTable = null;
  }
  let cupFit = missing(product.cupFit) ? {} : product.cupFit;
  if (!validCup(cupFit, rules)) {
    issue("CUP_MAP_UNAVAILABLE");
    cupFit = {};
  }
  let overrides = missing(product.overrides) ? null : product.overrides;
  if (overrides && !validOverrides(overrides)) {
    issue("OVERRIDES_UNAVAILABLE");
    overrides = null;
  }
  return { ...product, rules, sizeTable, cupFit, overrides };
}
function validateCatalog(catalog) {
  return validateCatalogData(catalog, true);
}
function validateCatalogData(catalog, requireAsOf) {
  const errors = [], warnings = [];
  const fail = (code, productId = null) => errors.push({ code, productId });
  if (!safeTree(catalog)) return { valid: false, errors: [{ code: "UNSAFE_CATALOG", productId: null }], warnings, catalog: null };
  if (!record(catalog) || catalog.schemaVersion !== SCHEMA || requireAsOf && !date(catalog.asOf) || !Array.isArray(catalog.products) || catalog.products.length > 1e3) {
    return { valid: false, errors: [{ code: "INVALID_CATALOG", productId: null }], warnings, catalog: null };
  }
  const ids = /* @__PURE__ */ new Set(), handles = /* @__PURE__ */ new Set(), products = [];
  for (const product of catalog.products) {
    if (!record(product) || !id(product.id) || !text(product.title, 512) || !text(product.handle, 192) || !/^[a-z0-9][a-z0-9-]*$/.test(product.handle) || !validUrl(product.url, product.handle) || !missing(product.updatedAt) && !date(product.updatedAt) || product.source !== "registered_product_guide" || !Array.isArray(product.optionNames) || !product.optionNames.length || product.optionNames.length > 3 || !product.optionNames.every((name) => text(name, 64)) || new Set(product.optionNames).size !== product.optionNames.length || !Number.isInteger(product.sizeOptionIndex) || product.sizeOptionIndex < -1 || product.sizeOptionIndex === -1 && !missing(product.rules) || product.sizeOptionIndex >= product.optionNames.length || !Array.isArray(product.variants) || product.variants.length > 1e3) {
      fail("INVALID_PRODUCT", id(product?.id) ? product.id : null);
      continue;
    }
    if (ids.has(product.id) || handles.has(product.handle)) {
      fail("DUPLICATE_PRODUCT", product.id);
      continue;
    }
    ids.add(product.id);
    handles.add(product.handle);
    const variantIds = /* @__PURE__ */ new Set(), variantOptions = /* @__PURE__ */ new Set();
    if (product.variants.some((variant) => {
      if (!record(variant) || !id(variant.id) || typeof variant.available !== "boolean" || !Array.isArray(variant.options) || variant.options.length !== product.optionNames.length || !variant.options.every((option) => text(option, 128)) || variantIds.has(variant.id) || variantOptions.has(JSON.stringify(variant.options))) return true;
      variantIds.add(variant.id);
      variantOptions.add(JSON.stringify(variant.options));
      return false;
    })) {
      fail("INVALID_VARIANTS", product.id);
      continue;
    }
    const projected = projectProduct(product, warnings);
    const labels = new Set(product.variants.map((variant) => variant.options[product.sizeOptionIndex]));
    if (projected.rules && projected.rules.sizes.some((size) => !labels.has(size.label))) {
      warnings.push({ code: "GUIDE_VARIANT_MISMATCH", productId: product.id });
      projected.rules = null;
    }
    products.push(projected);
  }
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    catalog: errors.length ? null : { ...catalog, products }
  };
}
function validBody(body) {
  return missing(body) || record(body) && Object.keys(body).every((part) => PARTS.includes(part)) && PARTS.every((part) => missing(body[part]) || [0, 1, 2].includes(body[part]));
}
function baseResult() {
  return {
    status: "no_data",
    recommendedSize: null,
    referenceSize: null,
    secondSize: null,
    candidates: [],
    sourceStatus: "NO_DATA",
    sourceReason: null,
    reasons: [],
    bodyChecks: [],
    sizes: [],
    blockingNotes: [],
    usedInputs: { height: null, weight: null, braSizeApplied: false, bodyChecks: [] },
    bodyInfluencesSize: false,
    measurementsComparison: [],
    basisSize: null,
    adjustmentSteps: []
  };
}
function numericMeasurement(value) {
  if (finite(value)) return value > 0;
  if (typeof value !== "string") return false;
  const match = /^(?:約\s*)?(\d+(?:\.\d+)?)(?:\s*[-‐–—〜～~]\s*(\d+(?:\.\d+)?))?\s*(?:cm|㎝|センチ)?$/i.exec(value.trim());
  return !!match && Number(match[1]) > 0 && (!match[2] || Number(match[2]) >= Number(match[1]));
}
var bodyReferenceDimension = (dim) => /対応|目安|ヌード|身体|人体|適応|body/i.test(dim);
function bodyChecksFor(product, body) {
  const dims = /* @__PURE__ */ new Set();
  const rules = product.rules;
  for (const dim of rules?.measurement_dims || []) {
    if (rules.sizes.some((size) => own(size.measurements || {}, dim) && numericMeasurement(size.measurements[dim]))) dims.add(dim);
  }
  const sizeLabels = /* @__PURE__ */ new Set([
    ...(rules?.sizes || []).map((size) => size.label),
    ...product.variants.map((variant) => variant.options[product.sizeOptionIndex])
  ]);
  const table = product.sizeTable;
  const dimensionLabel = (label) => !sizeLabels.has(label) && !/^(?:サイズ|size)(?:[（(].*[）)])?$/i.test(label);
  for (const [index, dim] of (table?.headers || []).entries()) {
    if (dimensionLabel(dim) && table.rows.some((row) => numericMeasurement(row.values[index]))) dims.add(dim);
  }
  for (const row of table?.rows || []) {
    if (dimensionLabel(row.label) && row.values.some((value, index) => index > 0 && numericMeasurement(value))) dims.add(row.label);
  }
  const applicable = rules?.category === "bottom" ? ["abdomen", "hip"] : PARTS;
  return applicable.filter((part) => !missing(body?.[part])).map((part) => {
    let dimensions = [...dims].filter((dim) => DIMENSIONS[part].test(dim));
    let indirect = false;
    if (!dimensions.length && rules.category !== "bottom" && ["abdomen", "hip"].includes(part)) {
      const hems = [...dims].filter((dim) => /裾幅|裾囲|hem/i.test(dim) && !bodyReferenceDimension(dim));
      const lengths = [...dims].filter((dim) => /着丈|総丈|length/i.test(dim) && !bodyReferenceDimension(dim));
      if (hems.length && lengths.length) {
        dimensions = [...hems, ...lengths];
        indirect = true;
      }
    }
    const chosen = CHOICES[part][body[part]];
    const bodyDims = dimensions.filter(bodyReferenceDimension);
    const garmentDims = dimensions.filter((dim) => !bodyReferenceDimension(dim));
    let detail;
    if (indirect) {
      const names = `「${dimensions.join("」「")}」`;
      detail = part === "abdomen" ? `${names}は、着丈から裾の位置を確かめ、裾がお腹に重なる場合に、裾幅を着やすいお手持ちの服と比べるための参考です。身体のウエスト寸法や、お腹のゆとりを示す数値ではありません。` : `ヒップまでかかる丈の上衣の場合だけ、${names}で裾の位置と幅を確かめ、着やすいお手持ちの服と比べてみてください。ヒップそのものの寸法やフィットを示す数値ではありません。`;
    } else if (dimensions.length) {
      const pieces = [];
      if (bodyDims.length) pieces.push(`「${bodyDims.join("」「")}」は、商品に記載の採寸方法に沿って、ご自身の身体寸法と照らし合わせてください。`);
      if (garmentDims.length) pieces.push(`「${garmentDims.join("」「")}」は、着やすいお手持ちの服と同じ測り方で比べてみてください。服の寸法を身体の寸法としては扱っていません。`);
      detail = pieces.join(" ");
    } else {
      detail = `この商品には${PART_LABELS[part]}を確かめる採寸項目がありません。気になる場合は、採寸方法と寸法をご確認ください。`;
    }
    if (part === "bust" && body[part] === 2 && Object.values(product.cupFit || {}).some((values2) => values2.length)) {
      detail += " 胸まわりが気になる方は、この商品のブラサイズ対応表もご確認ください。";
    }
    return {
      part,
      choice: body[part],
      label: `${PART_LABELS[part]}：${chosen}`,
      text: detail,
      dimensions,
      missing: dimensions.length === 0,
      indirect,
      measurementKinds: dimensions.map((dimension) => ({
        dimension,
        kind: bodyReferenceDimension(dimension) ? "body_reference" : "garment"
      }))
    };
  });
}
function scopedSizes(product, scope) {
  const ready = Array.isArray(scope) && scope.length === product.optionNames.length && scope.every((value, index) => index === product.sizeOptionIndex ? value === null : text(value, 128));
  return (product.rules?.sizes || []).map((size) => {
    const matches = ready ? product.variants.filter((variant2) => variant2.options.every((option, index) => index === product.sizeOptionIndex ? option === size.label : option === scope[index])) : [];
    const variant = matches.length === 1 ? matches[0] : null;
    return {
      label: size.label,
      heightRange: size.height_range,
      weightRange: size.weight_range,
      measurements: size.measurements || {},
      available: variant?.available === true,
      variantId: variant?.id || null
    };
  });
}
function rangeReason(kind, value, guide, label, unit, adjusted) {
  const matched = value >= guide.min && value <= guide.max;
  return { kind, matched, text: `${kind === "height" ? "身長" : "体重"}${value}${unit}は、${adjusted ? "調整前に照合した" : ""}${label}の目安${guide.min}〜${guide.max}${unit}${matched ? "に含まれています。" : "の範囲外です。"}` };
}
function recommendBodyChoice(product, input) {
  const output = baseResult();
  if (!safeTree(input) || !record(input) || !finite(input.height) || !finite(input.weight) || input.height < 130 || input.height > 200 || input.weight < 30 || input.weight > 120 || !validBody(input.body) || !missing(input.braSize) && typeof input.braSize !== "string") {
    output.status = "invalid";
    output.sourceStatus = "INVALID_INPUT";
    output.blockingNotes.push("身長は130〜200cm、体重は30〜120kgの数値で入力してください。体型の選択も見直してください。");
    return output;
  }
  output.usedInputs.height = input.height;
  output.usedInputs.weight = input.weight;
  if (!safeTree(product) || !record(product) || !validRules(product.rules) || !Array.isArray(product.optionNames) || !Number.isInteger(product.sizeOptionIndex) || !Array.isArray(product.variants)) {
    output.blockingNotes.push("この商品には、身長・体重からサイズを選ぶための目安表がありません。商品のサイズ表をご確認ください。");
    return output;
  }
  const checked = validateCatalogData({ schemaVersion: SCHEMA, products: [product] }, false);
  if (!checked.valid || !checked.catalog.products[0].rules) {
    output.blockingNotes.push("商品情報を確認できないため、サイズをおすすめできません。");
    return output;
  }
  const p = checked.catalog.products[0], rules = p.rules;
  output.bodyChecks = bodyChecksFor(p, input.body);
  output.usedInputs.bodyChecks = output.bodyChecks.map((check) => check.part);
  output.sizes = scopedSizes(p, input.scopeOptions);
  const raw = recommendSize(rules, input.height, input.weight);
  const basis = raw.recommended || raw.nearest || null;
  output.basisSize = basis?.label || null;
  const evidence = applyEvidenceOverride(rules, raw, p.overrides, input.height, input.weight);
  const cup = buildCupIndex(p.cupFit);
  const braSize = (input.braSize || "").trim().toUpperCase();
  const cupLabel = own(cup.reverse, braSize) ? cup.reverse[braSize] : null;
  const result = applyCupAdjustment(rules, evidence, cup.reverse, braSize);
  if (evidence.reason === "EVIDENCE") output.adjustmentSteps.push({
    kind: "evidence",
    from: evidence.overriddenFrom,
    to: evidence.nearest.label
  });
  if (result.adjusted === "up") output.adjustmentSteps.push(raw.status === "MULTIPLE_MATCH_ASK" ? {
    kind: "cup",
    from: null,
    fromCandidates: raw.candidates.map((size) => size.label),
    to: (result.recommended || result.nearest).label
  } : { kind: "cup", from: result.cupBase, to: (result.recommended || result.nearest).label });
  output.sourceStatus = result.status;
  output.sourceReason = result.reason || null;
  output.usedInputs.braSizeApplied = !!cupLabel;
  const current = result.recommended || result.nearest || null;
  if (result.status === "MULTIPLE_MATCH_ASK") {
    output.status = "choice";
    output.candidates = (result.candidates || []).map((size) => size.label);
    output.reasons.push({
      kind: "range",
      matched: null,
      text: `身長・体重の目安が${output.candidates.join("・")}で重なっています。商品の寸法を比べてお選びください。`
    });
  } else if (result.status === "OUT_OF_RANGE") {
    output.status = "outside";
    if (result.reason !== "BOTH_ABOVE") output.referenceSize = current?.label || null;
    if (result.reason !== "EVIDENCE") output.reasons.push({ kind: "range", matched: false, text: result.reason === "BOTH_ABOVE" ? "身長・体重が、この商品の最大サイズの目安をどちらも超えています。ひとつのサイズをおすすめできません。" : "身長と体重を同時に含むサイズが、目安表にありません。表示するサイズは確認用の参考です。" });
  } else if (["SINGLE_MATCH", "BOUNDARY_ROUND"].includes(result.status)) {
    output.status = "recommended";
    output.recommendedSize = current.label;
    if (result.status === "BOUNDARY_ROUND" && result.adjusted !== "up") output.reasons.push({
      kind: "range",
      matched: true,
      text: `複数サイズの目安が重なるため、この商品の登録ルールに沿って${current.label}を表示しています。`
    });
  }
  const rangeBases = result.status === "MULTIPLE_MATCH_ASK" ? result.candidates : raw.status === "MULTIPLE_MATCH_ASK" ? raw.candidates : basis ? [basis] : [];
  if (result.reason !== "BOTH_ABOVE") for (const rangeBase of rangeBases) {
    const adjusted = output.adjustmentSteps.length > 0;
    output.reasons.push(rangeReason("height", input.height, rangeBase.height_range, rangeBase.label, "cm", adjusted));
    output.reasons.push(rangeReason("weight", input.weight, rangeBase.weight_range, rangeBase.label, "kg", adjusted));
  }
  if (evidence.reason === "EVIDENCE") output.reasons.push({
    kind: "evidence",
    matched: null,
    text: `${evidence.overriddenFrom}から${evidence.nearest.label}へ、AYUNELのトップス全体のレビュー集計に基づく参考調整を反映しました。この商品自体の試着結果ではありません。`
  });
  if (cupLabel) output.reasons.push({ kind: "cup", matched: true, text: result.reason === "BOTH_ABOVE" ? "ブラサイズの対応は確認しましたが、身長・体重が目安を超えているため、この対応だけではサイズをおすすめできません。" : result.adjusted === "up" ? raw.status === "MULTIPLE_MATCH_ASK" ? `身長・体重では${raw.candidates.map((size) => size.label).join("・")}が候補でした。選んだブラサイズの対応表に沿って、${current.label}を表示しています。` : `選んだブラサイズの対応を確認し、${result.cupBase}より大きい${current.label}を表示しています。` : `選んだブラサイズの対応表を確認しました。${cupLabel}より小さいサイズは候補から除いています。` });
  else if (braSize || input.body?.bust === 2 && rules.category !== "bottom") output.reasons.push({
    kind: "cup",
    matched: false,
    text: braSize ? "選んだブラサイズの対応がこの商品にないため、ブラサイズは判定に使っていません。" : cup.options.length ? "ブラサイズが未選択のため、胸まわりの適合は確認できていません。体型の印象だけでサイズは変更していません。" : "この商品にはブラサイズの対応表がないため、胸まわりの適合は確認できていません。商品に掲載された胸まわりの寸法がある場合は、着やすいお手持ちの服と比べてみてください。体型の印象だけでサイズは変更していません。"
  });
  const reference = output.recommendedSize || output.referenceSize;
  if (reference) {
    const order = rules.sizes.map((size) => size.label);
    const floorIdx = Math.max(
      cupLabel ? order.indexOf(cupLabel) : -1,
      result.adjusted === "up" && result.cupBase ? order.indexOf(result.cupBase) : -1
    );
    const second = pickSecondSize(rules, reference, input.height, input.weight, {
      overriddenFrom: result.overriddenFrom || null,
      alternativeLabel: result.adjusted !== "up" ? result.alternative?.label || null : null,
      floorIdx
    });
    output.secondSize = second?.label || null;
    if (second) output.measurementsComparison = compareSizeMeasurements(rules, current, second);
  }
  if (output.sizes.some((size) => size.variantId === null)) output.blockingNotes.push("色などの条件が未選択、または該当する組み合わせがないため、一部のサイズを選択できません。");
  if (reference && output.sizes.find((size) => size.label === reference)?.available !== true) {
    output.blockingNotes.push("表示中の参考サイズは、現在の条件では選択できません。在庫によって別のサイズへ変更はしていません。");
  }
  return output;
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-copy.js
function inside(value, range2) {
  const min = Array.isArray(range2) ? range2[0] : range2?.min;
  const max = Array.isArray(range2) ? range2[1] : range2?.max;
  return Number.isFinite(value) && Number.isFinite(min) && Number.isFinite(max) && value >= min && value <= max;
}
function matchesBoth(result, label) {
  const size = (result.sizes || []).find((item) => item.label === label);
  return Boolean(size && inside(result.usedInputs?.height, size.heightRange) && inside(result.usedInputs?.weight, size.weightRange));
}
var shortBlocking = /* @__PURE__ */ new Map([
  ["身長は130〜200cm、体重は30〜120kgの数値で入力してください。体型の選択も見直してください。", "身長130〜200cm・体重30〜120kgで入力し、体型の選択も見直してください。"],
  ["この商品には、身長・体重からサイズを選ぶための目安表がありません。商品のサイズ表をご確認ください。", "この商品には身長・体重の目安がありません。商品ページのサイズ表をご覧ください。"],
  ["商品情報を確認できないため、サイズをおすすめできません。", "商品情報を確認できません。商品ページのサイズ表をご覧ください。"],
  ["色などの条件が未選択、または該当する組み合わせがないため、一部のサイズを選択できません。", "色・種類が未選択か、組み合わせがないため、選べないサイズがあります。"],
  ["表示中の参考サイズは、現在の条件では選択できません。在庫によって別のサイズへ変更はしていません。", "表示中のサイズは現在選べません。在庫に合わせて別のサイズには変えていません。"]
]);
function cupWarning(reason) {
  if (reason.text.startsWith("選んだブラサイズの対応がこの商品にないため、")) return "このブラサイズは対応表にないため、判定に使っていません。";
  if (reason.text.startsWith("ブラサイズが未選択のため、")) return "ブラサイズが未選択のため、胸まわりは確認できていません。";
  if (reason.text.startsWith("この商品にはブラサイズの対応表がないため、")) return "ブラサイズの対応表がないため、胸まわりは確認できていません。";
  return reason.text;
}
function resultCopy(result) {
  const adjustments = result.adjustmentSteps || [];
  const evidence = result.sourceReason === "EVIDENCE" || adjustments.some((item) => item.kind === "evidence");
  const cupAdjusted = adjustments.some((item) => item.kind === "cup");
  const outside = result.status === "outside" && result.sourceReason !== "EVIDENCE";
  const size = result.recommendedSize || result.referenceSize || null;
  const notes = [];
  let primary = size;
  let label = outside ? "参考サイズ" : "サイズの目安";
  if (result.status === "choice") {
    primary = (result.candidates || []).join(" / ");
    label = "候補";
    notes.push("ひとつに絞れませんでした。実寸を比べてお選びください。");
  } else if (result.sourceReason === "BOTH_ABOVE") {
    primary = null;
    label = "この表ではサイズをご案内できません";
    notes.push("身長・体重とも、最大サイズの目安を上回っています。");
  } else if (result.status === "no_data") {
    primary = null;
    label = "サイズの目安を出せません";
  } else if (result.status === "invalid") {
    primary = null;
    label = "入力を確認してください";
  } else {
    if (outside && !size) label = "この表ではサイズをご案内できません";
    if (cupAdjusted) {
      const first = adjustments[0];
      const basis = result.basisSize || first?.fromCandidates?.join(" / ") || first?.from;
      const chain = basis ? [`身長・体重：${basis}`] : [];
      adjustments.forEach((item) => {
        chain.push(`${item.kind === "evidence" ? "レビュー" : "ブラサイズ対応"}：${item.to}`);
      });
      notes.push(chain.join(" → "));
      notes.push("ブラサイズ対応表をもとに調整しています。");
    } else if (result.status === "recommended" && matchesBoth(result, size)) {
      notes.push(`身長・体重とも、${size} の目安に入ります。`);
    }
    if (evidence) {
      notes.push("複数のトップスのレビューも参考にしています。この商品を試着した結果ではありません。");
    }
    if (outside) notes.push(size ? `身長と体重の両方に合うサイズが表にありません。${size} は比べるための参考です。` : "身長と体重の両方に合うサイズが表にありません。実寸を見てお選びください。");
    if (!cupAdjusted && result.sourceStatus === "BOUNDARY_ROUND" && result.secondSize && matchesBoth(result, result.secondSize)) {
      notes.push(`${result.secondSize} の目安とも重なります。迷うときは実寸を比べてください。`);
    }
  }
  (result.reasons || []).filter((reason) => reason.kind === "cup" && reason.matched === false).forEach((reason) => notes.push(cupWarning(reason)));
  (result.blockingNotes || []).forEach((text2) => notes.push(shortBlocking.get(text2) || text2));
  return { primary, label, referenceOnly: outside && Boolean(size), notes: [...new Set(notes)] };
}
function sizeTabMark(result, label) {
  if (result.status === "choice" && (result.candidates || []).includes(label)) return "候補";
  if (label !== (result.recommendedSize || result.referenceSize)) return "";
  return result.status === "outside" && result.sourceReason !== "EVIDENCE" ? "参考" : "目安";
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-ui.js
var groups = [
  { key: "shoulder", title: "肩幅", view: "正面", description: "首のつけ根から肩先までの印象", options: ["狭め", "中間", "広め"] },
  { key: "bust", title: "バスト", view: "横から", description: "胸元のふくらみの印象", options: ["小さめ", "中間", "大きめ"] },
  { key: "abdomen", title: "お腹まわり", view: "横から", description: "お腹の前側の丸みの印象", options: ["平らに近い", "少し丸み", "丸みがある"] },
  { key: "hip", title: "ヒップ", view: "後ろから", description: "腰からヒップにかけての幅の印象", options: ["幅が狭め", "中間", "幅が広め"] }
];
var layout = { width: 941, height: 1672, cellHeight: 412, rowTops: [0, 414, 828, 1242] };
function mountBodyChoice(root, options = {}) {
  if (!root || typeof root.querySelector !== "function") throw new TypeError("A panel root is required");
  const ownerDocument = root.ownerDocument || root;
  const surface = root.matches?.(".ayunel-body-options") ? root : root.querySelector(".ayunel-body-options");
  if (!surface) throw new TypeError("The body-choice panel markup is required");
  const byId = (id3) => root.querySelector(`[id="ayunel-${id3}"]`);
  const form = byId("size-form");
  const productSelect = byId("product");
  const heightInput = byId("height");
  const weightInput = byId("weight");
  const braSelect = byId("bra-size");
  if (!form || !productSelect || !heightInput || !weightInput || !braSelect) throw new TypeError("Incomplete body-choice panel");
  const controller = new AbortController();
  const listen = (target, type, callback, config = {}) => target.addEventListener(type, callback, { ...config, signal: controller.signal });
  const embedded = options.embedded === true;
  const onSelect = typeof options.onSelect === "function" ? options.onSelect : null;
  let catalog = null;
  let product = null;
  let result = null;
  let comparisonSize = null;
  let step = 1;
  let scopeOptions = [];
  let contextRevision = 0;
  let selecting = false;
  let destroyed = false;
  surface.dataset.embedded = String(embedded);
  const selectionNotice = () => embedded ? "商品ページでサイズを選びます。カートには入りません。" : "このプレビュー内の選択です。商品ページやカートには反映されません。";
  function node(tag, text2, className) {
    const element = ownerDocument.createElement(tag);
    if (text2 !== void 0 && text2 !== null) element.textContent = String(text2);
    if (className) element.className = className;
    return element;
  }
  function setStep(next, focus = true) {
    surface.dataset.stepMotion = focus && next !== step ? next > step ? "forward" : "backward" : "none";
    step = next;
    surface.dataset.step = String(next);
    for (let number = 1; number <= 3; number += 1) {
      byId(`step-${number}`).hidden = number !== next;
      const marker = root.querySelector(`[data-step-marker="${number}"]`);
      if (number === next) marker.setAttribute("aria-current", "step");
      else marker.removeAttribute("aria-current");
    }
    if (focus) byId(next === 3 ? "result-title" : `step-${next}-title`).focus();
  }
  function clearResult() {
    contextRevision += 1;
    selecting = false;
    result = null;
    comparisonSize = null;
    root.querySelectorAll(".ayunel-more details").forEach((details) => {
      details.open = false;
    });
    for (const id3 of ["result-product", "result-overview", "reasons", "second-comparison", "input-summary", "body-checks", "size-tabs", "chart", "comparison", "source", "inventory-note"]) byId(id3).replaceChildren();
    byId("second-section").hidden = true;
    byId("local-select").disabled = true;
    byId("local-select").removeAttribute("aria-busy");
    byId("local-select").textContent = "サイズを選んでください";
    byId("local-selection").textContent = selectionNotice();
    if (step === 3) setStep(1, false);
  }
  function readBody() {
    return Object.fromEntries(groups.map((group) => {
      const checked = form.querySelector(`input[name="${group.key}"]:checked`);
      return [group.key, relevantGroups().includes(group.key) && checked ? Number(checked.value) : null];
    }));
  }
  function relevantGroups() {
    return (product?.category || product?.rules?.category) === "bottom" ? ["abdomen", "hip"] : groups.map((group) => group.key);
  }
  function readScope() {
    if (embedded) return (product?.optionNames || []).map((_, index) => index === product.sizeOptionIndex ? null : scopeOptions[index] || null);
    return (product?.optionNames || []).map((_, index) => index === product.sizeOptionIndex ? null : form.querySelector(`[data-scope-index="${index}"]`)?.value || null);
  }
  function readInput() {
    return { height: heightInput.value === "" ? null : Number(heightInput.value), weight: weightInput.value === "" ? null : Number(weightInput.value), body: readBody(), braSize: braSelect.value, scopeOptions: readScope() };
  }
  function updateCount() {
    byId("selection-count").textContent = `選択：${Object.values(readBody()).filter((value) => value !== null).length} / ${relevantGroups().length} 項目`;
  }
  function renderGroups() {
    byId("groups").replaceChildren();
    groups.forEach((group, row) => {
      const fieldset = node("fieldset", null, "ayunel-group");
      fieldset.dataset.bodyGroup = group.key;
      fieldset.innerHTML = `<legend>${group.title}<span class="ayunel-group-view">${group.view}</span></legend>
      <p class="ayunel-group-description" id="ayunel-${group.key}-description">${group.description}</p>
      <div class="ayunel-options">${group.options.map((label, index) => `<label class="ayunel-option">
        <input class="ayunel-radio" type="radio" name="${group.key}" value="${index}" aria-describedby="ayunel-${group.key}-description">
        <span class="ayunel-option-content"><span class="ayunel-illustration" aria-hidden="true"><span class="ayunel-illustration-tile" style="--ayunel-art-x:${index * 50}%;--ayunel-art-y:${layout.rowTops[row] * 100 / (layout.height - layout.cellHeight)}%"></span><span class="ayunel-illustration-message">イラストを読み込み中…</span></span>
        <span class="ayunel-option-caption">${label}</span><span class="ayunel-option-circle" aria-hidden="true"><svg class="ayunel-option-check" viewBox="0 0 16 16" focusable="false"><path d="M3 8 L6.5 11.5 L13 4.5"/></svg></span></span></label>`).join("")}</div>
      <button class="ayunel-skip" type="button" data-clear-group="${group.key}" aria-label="${group.title}：わからない・選ばない。選択をクリア">わからない・選ばない</button>`;
      byId("groups").append(fieldset);
    });
  }
  function loadIllustration() {
    surface.dataset.illustrationState = "loading";
    const atlas = new Image();
    atlas.decoding = "async";
    const failed = () => {
      if (destroyed) return;
      surface.dataset.illustrationState = "error";
      root.querySelectorAll(".ayunel-illustration-message").forEach((message) => {
        message.textContent = "イラストを表示できません";
      });
    };
    listen(atlas, "load", async () => {
      try {
        if (typeof atlas.decode === "function") await atlas.decode();
        if (destroyed) return;
        if (atlas.naturalWidth !== layout.width || atlas.naturalHeight !== layout.height) throw new Error("Illustration layout mismatch");
        surface.style.setProperty("--ayunel-illustration-cell-ratio", String(atlas.naturalWidth / (3 * layout.cellHeight)));
        surface.style.setProperty("--ayunel-illustration-atlas-height", `${atlas.naturalHeight * 100 / layout.cellHeight}%`);
        surface.dataset.illustrationState = "ready";
      } catch {
        failed();
      }
    }, { once: true });
    listen(atlas, "error", failed, { once: true });
    try {
      const url = new URL(options.illustrationUrl || "./assets/body-illustrations.png", import.meta.url);
      if (url.origin !== ownerDocument.location.origin || !["http:", "https:"].includes(url.protocol)) throw new Error("Illustration must be same origin");
      surface.style.setProperty("--ayunel-illustration-url", `url("${url.href.replace(/["\\\n\r]/g, "")}")`);
      atlas.src = url.href;
    } catch {
      failed();
    }
  }
  function renderScopes() {
    const container = byId("variant-scopes");
    container.replaceChildren();
    container.hidden = embedded;
    if (embedded) return;
    (product?.optionNames || []).forEach((name, index) => {
      if (index === product.sizeOptionIndex) return;
      const wrapper = node("div", null, "ayunel-field");
      const select = node("select");
      select.id = `ayunel-scope-${index}`;
      select.dataset.scopeIndex = String(index);
      select.name = `scope-${index}`;
      const label = node("label", name, "ayunel-input-label");
      label.htmlFor = select.id;
      const empty = node("option", `${name}を選んでください`);
      empty.value = "";
      select.append(empty);
      const options2 = [...new Set((product.variants || []).map((variant) => variant.options[index]).filter((value) => typeof value === "string" && value))];
      options2.forEach((value) => {
        const option = node("option", value);
        option.value = value;
        select.append(option);
      });
      select.value = scopeOptions[index] || "";
      wrapper.append(label, select);
      container.append(wrapper);
    });
    if (container.children.length) container.append(node("p", "色・種類を選ぶと、在庫を確認できます。", "ayunel-detail"));
  }
  function braLabels() {
    if (!product?.cupFit || typeof product.cupFit !== "object") return [];
    return [...new Set(Object.values(product.cupFit).flat().filter((value) => typeof value === "string" && value))];
  }
  function renderProduct() {
    const info = byId("product-info");
    info.replaceChildren();
    info.hidden = !product;
    if (product) info.append(node("p", product.title));
    renderScopes();
    const previousBra = braSelect.value;
    braSelect.replaceChildren();
    const empty = node("option", "わからない・選ばない");
    empty.value = "";
    braSelect.append(empty);
    const labels = braLabels();
    labels.forEach((label) => {
      const option = node("option", label);
      option.value = label;
      braSelect.append(option);
    });
    braSelect.value = labels.includes(previousBra) ? previousBra : "";
    byId("bra-fieldset").hidden = labels.length === 0;
    byId("groups").querySelectorAll("[data-body-group]").forEach((fieldset) => {
      fieldset.hidden = !relevantGroups().includes(fieldset.dataset.bodyGroup);
    });
    const hipDescription = byId("hip-description");
    hipDescription.textContent = relevantGroups().length === 2 ? "腰からヒップにかけての幅の印象" : "ヒップにかかる丈のトップスで確認する、幅の印象";
    byId("body-relevance-note").textContent = relevantGroups().length === 2 ? "この商品では、お腹まわり・ヒップの実寸を確認します。" : "ヒップは、裾がヒップにかかる丈のときに確認します。";
    updateCount();
  }
  function inputValid() {
    const input = readInput();
    let message = "";
    let focus = null;
    if (!product) {
      message = "サイズを確かめる商品を選んでください。";
      focus = productSelect;
    } else if (input.height === null || !Number.isFinite(input.height)) {
      message = "身長を数字で入力してください。";
      focus = heightInput;
    } else if (input.height < 130 || input.height > 200) {
      message = "身長は 130〜200 cm の範囲で入力してください。";
      focus = heightInput;
    } else if (input.weight === null || !Number.isFinite(input.weight)) {
      message = "体重を数字で入力してください。";
      focus = weightInput;
    } else if (input.weight < 30 || input.weight > 120) {
      message = "体重は 30〜120 kg の範囲で入力してください。";
      focus = weightInput;
    }
    byId("input-error").textContent = message;
    byId("input-error").hidden = !message;
    heightInput.removeAttribute("aria-invalid");
    weightInput.removeAttribute("aria-invalid");
    productSelect.removeAttribute("aria-invalid");
    if (focus) {
      focus.setAttribute("aria-invalid", "true");
      focus.focus();
    }
    return !message;
  }
  function rangeText(range2, unit) {
    if (!range2) return "目安なし";
    if (Array.isArray(range2) && range2.length === 2) return `${range2[0]}〜${range2[1]} ${unit}`;
    if (typeof range2 === "object" && Number.isFinite(range2.min) && Number.isFinite(range2.max)) return `${range2.min}〜${range2.max} ${unit}`;
    return "目安なし";
  }
  function renderOverview() {
    const overview = byId("result-overview");
    overview.replaceChildren();
    const copy = resultCopy(result);
    overview.dataset.status = result.status;
    overview.dataset.referenceOnly = String(copy.referenceOnly);
    overview.append(node("p", copy.primary || copy.label, copy.primary ? "ayunel-result-size" : "ayunel-result-explanation ayunel-result-explanation--standalone"));
    if (copy.primary) overview.append(node("p", copy.label, "ayunel-result-explanation"));
    copy.notes.forEach((text2) => overview.append(node("p", text2, "ayunel-detail ayunel-result-note")));
  }
  function renderSecondComparison() {
    const reference = result.recommendedSize || result.referenceSize;
    const second = result.secondSize;
    byId("second-section").hidden = !reference || !second;
    if (!reference || !second) return;
    const container = byId("second-comparison");
    container.replaceChildren(node("p", `${reference} と ${second} の実寸`, "ayunel-detail"));
    if (result.measurementsComparison?.length) {
      const table = node("table");
      const unit = typeof product.rules?.unit?.measurement === "string" ? product.rules.unit.measurement : "";
      table.append(node("caption", `実寸の比較${unit ? `（${unit}）` : ""}`));
      const head = node("thead");
      const heading = node("tr");
      ["項目", reference, second].forEach((label) => {
        const cell = node("th", label);
        cell.scope = "col";
        heading.append(cell);
      });
      head.append(heading);
      const body = node("tbody");
      result.measurementsComparison.forEach((item) => {
        const row = node("tr");
        const label = node("th", item.dim);
        label.scope = "row";
        row.append(label, node("td", item.a ?? "—"), node("td", item.b ?? "—"));
        body.append(row);
      });
      table.append(head, body);
      container.append(table);
    } else container.append(node("p", "比較できる実寸の掲載がありません。", "ayunel-detail"));
    const button = node("button", `${second} の実寸を見る`, "ayunel-button ayunel-button-outline");
    button.type = "button";
    listen(button, "click", () => {
      compareSize(second);
      byId("chart-title").scrollIntoView({ block: "start" });
    });
    container.append(button);
  }
  function renderReasons() {
    const reasons = byId("reasons");
    reasons.replaceChildren();
    const titles = { height: "身長", weight: "体重", cup: "ブラサイズ", evidence: "参考にした情報", range: "目安の範囲" };
    (result.reasons || []).forEach((reason) => {
      const article = node("article", null, "ayunel-reason");
      article.append(node("h3", titles[reason.kind] || "確認したこと"), node("p", reason.text));
      reasons.append(article);
    });
    reasons.closest(".ayunel-result-section").hidden = !reasons.childElementCount;
  }
  function renderInputSummary(input) {
    const summary = byId("input-summary");
    summary.replaceChildren();
    const answers = [["身長", `${input.height} cm`], ["体重", `${input.weight} kg`], ...groups.filter((group) => relevantGroups().includes(group.key)).map((group) => [group.title, input.body[group.key] === null ? "未選択" : group.options[input.body[group.key]]])];
    if (!byId("bra-fieldset").hidden) answers.push(["普段のブラサイズ", input.braSize || "未選択"]);
    answers.forEach(([label, value]) => {
      const item = node("div", null, "ayunel-summary-item");
      item.append(node("dt", label), node("dd", value));
      summary.append(item);
    });
  }
  function renderBodyChecks() {
    const checks = byId("body-checks");
    checks.replaceChildren();
    (result.bodyChecks || []).forEach((check) => {
      const article = node("article", null, "ayunel-check");
      const group = groups.find((item) => item.key === check.part);
      article.append(node("h3", group?.title || check.label || "確認したい箇所"), node("p", check.text));
      if (check.dimensions?.length) article.append(node("p", `サイズ表で見る項目：${check.dimensions.join("・")}`, "ayunel-check-detail"));
      else if (check.missing) article.append(node("p", "この部位に対応する寸法の登録がありません。数値による確認はできません。", "ayunel-check-detail"));
      checks.append(article);
    });
    if (!checks.childElementCount) checks.append(node("p", Object.values(readBody()).some((value) => value !== null) ? "選んだ体型を照合できる商品情報が不足しています。商品サイズ表と、着やすい手持ちの服を比べてみてください。" : "体型の選択はありません。商品サイズ表と、着やすい手持ちの服を比べてみてください。", "ayunel-detail"));
  }
  function renderTable() {
    const container = byId("chart");
    container.replaceChildren();
    const sizeTable = product.sizeTable;
    if (!sizeTable?.headers?.length || !sizeTable.rows?.length) {
      container.append(node("p", "この商品の実寸表は掲載がありません。", "ayunel-detail"));
      return;
    }
    const table = node("table");
    table.append(node("caption", "商品寸法と対応サイズの目安（表内の単位・採寸方法を参照）"));
    const head = node("thead");
    const headRow = node("tr");
    sizeTable.headers.forEach((label) => {
      const cell = node("th", label);
      cell.scope = "col";
      headRow.append(cell);
    });
    head.append(headRow);
    const body = node("tbody");
    sizeTable.rows.forEach((row) => {
      const tr = node("tr");
      tr.dataset.sizeRow = row.label;
      const values2 = row.values || [];
      values2.forEach((value, index) => {
        const cell = node(index === 0 ? "th" : "td", value);
        if (index === 0) cell.scope = "row";
        tr.append(cell);
      });
      body.append(tr);
    });
    table.append(head, body);
    container.append(table);
  }
  function renderSizeButtons() {
    byId("size-tabs").replaceChildren();
    byId("inventory-note").textContent = embedded ? "" : `${formatDate(catalog.asOf)} 時点の在庫情報です。`;
    byId("inventory-note").hidden = embedded;
    (result.sizes || []).forEach((size) => {
      const availability = availabilityFor(size);
      const suffix = { unselected: "", unavailable: "（在庫切れ）", no_variant: "（該当なし）", available: "" }[availability];
      const button = node("button", `${size.label}${suffix}`, "ayunel-size-tab");
      const mark = sizeTabMark(result, size.label);
      if (mark) button.append(node("span", mark, "ayunel-size-tab-mark"));
      button.type = "button";
      button.dataset.compareSize = size.label;
      button.setAttribute("aria-pressed", "false");
      listen(button, "click", () => compareSize(size.label));
      byId("size-tabs").append(button);
    });
  }
  function availabilityFor(size) {
    const scope = readScope();
    if (!scope.every((value, index) => index === product.sizeOptionIndex || Boolean(value))) return "unselected";
    if (!size.variantId) return "no_variant";
    return size.available ? "available" : "unavailable";
  }
  function compareSize(label) {
    if (selecting) return;
    if (label === null) {
      comparisonSize = null;
      byId("size-tabs").querySelectorAll("button").forEach((button) => button.setAttribute("aria-pressed", "false"));
      byId("chart").querySelectorAll("[data-size-row]").forEach((row) => {
        row.dataset.compared = "false";
      });
      byId("comparison").replaceChildren(node("p", result?.sizes?.length ? "サイズを選ぶと、実寸を確認できます。" : "比較できるサイズ情報がありません。", "ayunel-detail"));
      byId("local-select").disabled = true;
      byId("local-select").textContent = "サイズを選んでください";
      byId("local-selection").textContent = selectionNotice();
      return;
    }
    const size = result?.sizes?.find((item) => item.label === label);
    if (!size) return;
    comparisonSize = label;
    byId("size-tabs").querySelectorAll("button").forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.compareSize === label)));
    byId("chart").querySelectorAll("[data-size-row]").forEach((row) => {
      row.dataset.compared = String(row.dataset.sizeRow === label);
    });
    const box = byId("comparison");
    box.replaceChildren(node("p", `${label} の目安（身長・体重）：${rangeText(size.heightRange, "cm")} ／ ${rangeText(size.weightRange, "kg")}`));
    const unit = typeof product.rules?.unit?.measurement === "string" ? product.rules.unit.measurement : "";
    box.append(node("h4", `${label} の実寸${unit ? `（${unit}）` : ""}`, "ayunel-current-measurements-title"));
    const dimensions = Object.entries(size.measurements || {}).filter(([, value]) => typeof value === "number" && Number.isFinite(value) || typeof value === "string" && !["", "-", "—", "–"].includes(value.trim()));
    if (dimensions.length) {
      const list = node("dl", null, "ayunel-current-measurements");
      dimensions.forEach(([dimension, value]) => {
        const item = node("div");
        item.append(node("dt", dimension), node("dd", value));
        list.append(item);
      });
      box.append(list);
    } else box.append(node("p", `${label} の実寸は掲載がありません。`, "ayunel-detail"));
    const availability = availabilityFor(size);
    const notes = {
      unselected: embedded ? "商品ページで色・種類を選び、もう一度ご確認ください。" : "入力画面で色・種類を選ぶと、在庫を確認できます。",
      unavailable: embedded ? `ページ表示時点では、この色・種類の ${label} は在庫切れです。別のサイズへ自動変更はしていません。` : `${formatDate(catalog.asOf)} 時点では、この色・種類の ${label} は在庫切れです。別のサイズへ自動変更はしていません。`,
      no_variant: `この色・種類に ${label} はありません。`
    };
    if (notes[availability]) box.append(node("p", notes[availability], "ayunel-detail"));
    const localButton = byId("local-select");
    localButton.removeAttribute("aria-busy");
    localButton.disabled = availability !== "available";
    localButton.textContent = localButton.disabled ? { unselected: "色・種類を選んでください", unavailable: `${label} は在庫切れです`, no_variant: `${label} は該当しません` }[availability] : embedded ? `${label} を商品ページで選ぶ` : `${label} をこのプレビュー内で選択`;
    byId("local-selection").textContent = selectionNotice();
  }
  function renderSource() {
    const source = byId("source");
    source.replaceChildren();
    source.append(node("p", "AYUNEL に登録されたサイズ目安と、商品ページのサイズ表を使っています。", "ayunel-source-line"));
    source.append(node("p", embedded ? "商品・在庫情報はページ表示時点のものです。選択時にもう一度確認します。" : `商品・在庫情報の確認日：${formatDate(catalog.asOf)}`, "ayunel-source-line"));
    source.append(node("p", "身長・体重の目安は、AYUNEL に登録された参考情報です。メーカーによる実測・試着結果とは異なります。", "ayunel-source-line"));
    source.append(node("p", "身長・体重の目安には、同じ種類の商品に共通する参考情報が含まれます。商品ページのサイズ表とは分けてご確認ください。", "ayunel-source-line"));
    if (product.updatedAt) source.append(node("p", `商品情報更新日：${formatDate(product.updatedAt)}`, "ayunel-source-line"));
    if (result.sourceReason === "EVIDENCE" || result.adjustmentSteps?.some((item) => item.kind === "evidence")) source.append(node("p", "レビューによる参考調整は、複数のトップスの集計を含みます。この商品だけの試着結果ではありません。", "ayunel-source-line"));
    source.append(node("p", "サイズを切り替えても、実際の試着状態や身体に対するゆとりは再現していません。", "ayunel-source-line"));
    source.append(node("p", "実寸は、着やすいお手持ちの服と同じ測り方で比べてみてください。", "ayunel-source-line"));
  }
  function formatDate(value) {
    const date2 = new Date(value);
    if (!Number.isFinite(date2.getTime())) return "確認日なし";
    return new Intl.DateTimeFormat("ja-JP", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Tokyo" }).format(date2);
  }
  function showRecommendation() {
    clearResult();
    const input = readInput();
    try {
      result = recommendBodyChoice(product, input);
    } catch {
      byId("input-error").textContent = "商品情報の照合ができませんでした。商品サイズ表をご確認ください。";
      byId("input-error").hidden = false;
      setStep(1);
      return;
    }
    byId("result-product").textContent = product.title;
    renderOverview();
    renderReasons();
    renderSecondComparison();
    renderInputSummary(input);
    renderBodyChecks();
    renderTable();
    renderSizeButtons();
    renderSource();
    const initial = result.recommendedSize || result.referenceSize || null;
    compareSize(initial);
    setStep(3);
  }
  listen(productSelect, "change", () => {
    const hadProduct = Boolean(product);
    clearResult();
    product = catalog?.products.find((item) => String(item.id) === productSelect.value) || null;
    scopeOptions = [];
    braSelect.value = "";
    renderProduct();
    byId("product-change-note").hidden = !hadProduct;
    byId("input-error").hidden = true;
  });
  listen(form, "input", () => {
    clearResult();
    updateCount();
  });
  listen(form, "change", () => {
    clearResult();
    updateCount();
  });
  listen(byId("groups"), "click", (event) => {
    const button = event.target.closest("button[data-clear-group]");
    if (!button) return;
    form.querySelectorAll(`input[name="${button.dataset.clearGroup}"]`).forEach((input) => {
      input.checked = false;
    });
    clearResult();
    updateCount();
  });
  listen(byId("next"), "click", () => {
    if (inputValid()) setStep(2);
  });
  listen(byId("back-input"), "click", () => setStep(1));
  listen(byId("edit"), "click", () => {
    clearResult();
    setStep(1);
  });
  listen(byId("edit-body"), "click", () => {
    clearResult();
    setStep(2);
  });
  listen(form, "submit", (event) => {
    event.preventDefault();
    if (inputValid()) showRecommendation();
    else setStep(1);
  });
  listen(byId("local-select"), "click", async () => {
    const size = result?.sizes?.find((item) => item.label === comparisonSize);
    if (selecting || !size?.available || !size.variantId || availabilityFor(size) !== "available") return;
    if (!embedded) {
      byId("local-selection").textContent = `${size.label} をこのプレビュー内で選択しました。商品ページやカートには反映されていません。`;
      return;
    }
    const button = byId("local-select");
    const revision = contextRevision;
    const payload = { size: size.label, variantId: size.variantId, productId: product.id, scopeOptions: readScope() };
    selecting = true;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = "商品ページのサイズ選択を確認しています…";
    byId("local-selection").textContent = "商品ページへの反映を確認しています。";
    let confirmed = false;
    try {
      confirmed = onSelect ? await onSelect(payload) === true : false;
    } catch {
      confirmed = false;
    }
    if (destroyed || revision !== contextRevision) return;
    selecting = false;
    button.removeAttribute("aria-busy");
    if (confirmed) {
      button.textContent = `${size.label}サイズを選択しました`;
      button.disabled = true;
      byId("local-selection").textContent = `${size.label}サイズを商品ページで選択しました。カートには入れていません。`;
    } else {
      button.textContent = `${size.label}サイズを商品ページで選ぶ`;
      button.disabled = false;
      byId("local-selection").textContent = "商品ページへの反映を確認できませんでした。いったん閉じて、商品ページの選択サイズをご確認ください。";
    }
  });
  function setContext(next = {}) {
    if (destroyed) return;
    const previousId = product ? String(product.id) : null;
    const previousBra = braSelect.value;
    const requestedId = Object.hasOwn(next, "productId") ? String(next.productId ?? "") : previousId;
    let valid = true;
    if (Object.hasOwn(next, "catalog")) {
      const checked = validateCatalog(next.catalog);
      catalog = checked.valid ? checked.catalog : null;
      valid = checked.valid;
      productSelect.replaceChildren();
      const empty = node("option", "商品を選んでください");
      empty.value = "";
      productSelect.append(empty);
      (catalog?.products || []).forEach((item) => {
        const option = node("option", item.title);
        option.value = String(item.id);
        productSelect.append(option);
      });
    }
    clearResult();
    product = catalog?.products.find((item) => String(item.id) === requestedId) || null;
    productSelect.value = product ? String(product.id) : "";
    if (Object.hasOwn(next, "scopeOptions")) scopeOptions = Array.isArray(next.scopeOptions) ? [...next.scopeOptions] : [];
    else if (!product || previousId !== String(product.id)) scopeOptions = [];
    braSelect.value = previousId && product && previousId === String(product.id) ? previousBra : "";
    renderProduct();
    byId("input-error").hidden = true;
    byId("product-change-note").hidden = !previousId || previousId === String(product?.id);
    const loaded = valid && Boolean(catalog?.products.length) && (!embedded || Boolean(product));
    byId("load-status").hidden = loaded;
    byId("load-status").textContent = loaded ? "" : "商品サイズ表を読み込めませんでした。商品ページのサイズ表をご確認ください。";
    if (!loaded) byId("load-status").setAttribute("role", "alert");
    else byId("load-status").setAttribute("role", "status");
    form.hidden = !loaded;
  }
  function reset() {
    if (destroyed) return;
    form.reset();
    heightInput.value = "";
    weightInput.value = "";
    braSelect.value = "";
    form.querySelectorAll(".ayunel-radio").forEach((input) => {
      input.checked = false;
    });
    byId("input-error").textContent = "";
    byId("input-error").hidden = true;
    heightInput.removeAttribute("aria-invalid");
    weightInput.removeAttribute("aria-invalid");
    productSelect.removeAttribute("aria-invalid");
    byId("product-change-note").hidden = true;
    clearResult();
    setStep(1, false);
    if (product) productSelect.value = String(product.id);
    renderProduct();
  }
  renderGroups();
  if (embedded) {
    root.querySelector(".ayunel-purpose").textContent = "この機能では、入力内容を保存・送信しません。";
    byId("step-1-title").textContent = "身長・体重を教えてください";
    byId("back-input").textContent = "身長・体重に戻る";
    root.querySelector('[data-step-marker="1"] .ayunel-step-label').textContent = "身長・体重";
  }
  loadIllustration();
  setContext({ catalog: options.catalog, productId: options.productId, scopeOptions: options.scopeOptions });
  return {
    setContext,
    reset,
    destroy() {
      if (destroyed) return;
      reset();
      destroyed = true;
      contextRevision += 1;
      controller.abort();
    }
  };
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-native.js
var SCOPE = "product-card, dialog, .shopify-section";
var PRODUCT_SELECT = "shopify:product:select";
var CONTEXT_SETTLE_MS = 2500;
var SELECT_CONFIRM_MS = 1e4;
var POLL_MS = 50;
var id2 = (value) => String(value ?? "").split("/").pop();
var values = (nodes) => Array.from(nodes || []);
var same = (left, right) => !!left && !!right && left.length === right.length && left.every((value, index) => value === right[index]);
function createNativeBridge(host, {
  productId,
  optionNames,
  sizeOptionIndex,
  variants,
  onContextChange = () => {
  }
}) {
  const pid = id2(productId);
  const names = Array.isArray(optionNames) ? [...optionNames] : [];
  const catalog = Array.isArray(variants) ? variants.filter((variant) => id2(variant.id) && Array.isArray(variant.options) && variant.options.length === names.length) : [];
  const valid = !!pid && names.length > 0 && Number.isInteger(sizeOptionIndex) && sizeOptionIndex >= 0 && sizeOptionIndex < names.length && catalog.length > 0;
  const scope = host?.closest?.(SCOPE) || null;
  const view = host?.ownerDocument?.defaultView;
  let destroyed = false;
  let active = null;
  let ownDispatch = false;
  let lastContext = "";
  let refreshTimer = null;
  let refreshUntil = 0;
  const listeners = [];
  const listen = (node, type, listener, options) => {
    node?.addEventListener?.(type, listener, options);
    listeners.push(() => node?.removeEventListener?.(type, listener, options));
  };
  const isLive = () => !destroyed && valid && !!scope && host.isConnected !== false && scope.isConnected !== false && host.closest(SCOPE) === scope;
  const local = (node) => node?.closest?.(SCOPE) === scope;
  function sources() {
    if (!isLive()) return { picker: null, form: null, input: null, ambiguous: true };
    const sectionId = host.dataset?.sectionId;
    const matches = (node) => local(node) && id2(node.dataset?.productId) === pid && (!sectionId || node.dataset?.sectionId === sectionId);
    const pickers = values(scope.querySelectorAll("variant-picker, swatches-variant-picker-component")).filter(matches);
    const forms = values(scope.querySelectorAll("product-form-component")).filter(matches);
    const ambiguous = pickers.length > 1 || forms.length !== 1;
    const form = !ambiguous ? forms[0] : null;
    const inputs = values(form?.querySelectorAll('input[name="id"]'));
    return {
      picker: !ambiguous ? pickers[0] || null : null,
      form,
      input: inputs.length === 1 ? inputs[0] : null,
      ambiguous: ambiguous || inputs.length !== 1
    };
  }
  function selectedOptions(picker) {
    return names.map((name, index) => {
      const radios = values(picker?.querySelectorAll('input[type="radio"]')).filter((input) => input.dataset.optionName === name);
      if (radios.length) {
        const checked = radios.filter((input) => input.checked);
        return checked.length === 1 ? checked[0].value : null;
      }
      const selects = values(picker?.querySelectorAll("select")).filter((select2) => select2.name === `options[${name}]`);
      if (selects.length) return selects.length === 1 && selects[0].value !== "" ? selects[0].value : null;
      const unique = [...new Set(catalog.map((variant) => variant.options[index]))];
      return unique.length === 1 ? unique[0] : null;
    });
  }
  function readContext() {
    const source = sources();
    const options = !source.ambiguous ? selectedOptions(source.picker) : names.map(() => null);
    const variantId = !source.ambiguous ? id2(source.input?.value) || null : null;
    const matching = options.every((value) => value !== null) ? catalog.filter((variant) => same(variant.options, options)) : [];
    const current = matching.length === 1 && id2(matching[0].id) === variantId ? matching[0] : null;
    return {
      productId: pid,
      variantId,
      options,
      scopeOptions: options.map((value, index) => index === sizeOptionIndex ? null : value),
      size: options[sizeOptionIndex] ?? null,
      available: current?.available === true,
      ready: isLive() && !source.ambiguous && !!current
    };
  }
  function notify() {
    if (!isLive()) return;
    const context = readContext();
    const serialized = JSON.stringify(context);
    if (serialized !== lastContext) {
      lastContext = serialized;
      onContextChange(context);
    }
  }
  function refreshAfterNativeEvent() {
    if (!isLive()) return;
    refreshUntil = Date.now() + CONTEXT_SETTLE_MS;
    if (refreshTimer !== null) return;
    const tick = () => {
      refreshTimer = null;
      if (!isLive()) return;
      notify();
      if (Date.now() < refreshUntil) refreshTimer = setTimeout(tick, POLL_MS);
    };
    refreshTimer = setTimeout(tick, 0);
  }
  function relevant(event) {
    const picker = event.target?.closest?.("variant-picker, swatches-variant-picker-component");
    return !!picker && local(picker) && id2(picker.dataset?.productId) === pid;
  }
  const onChange = (event) => {
    if (!isLive() || !relevant(event)) return;
    if (!ownDispatch) active?.finish(false);
    notify();
    refreshAfterNativeEvent();
  };
  const onProductSelect = (event) => {
    if (!isLive() || !relevant(event)) return;
    const operation = active;
    if (!ownDispatch) active?.finish(false);
    notify();
    refreshAfterNativeEvent();
    if (event.promise?.then) {
      if (ownDispatch && operation) operation.nativePending = true;
      Promise.resolve(event.promise).then(
        (resolved) => {
          if (active === operation && operation) {
            if (resolved?.detail?.resource?.available === false) operation.finish(false);
            else operation.nativePending = false;
          }
          if (isLive()) {
            notify();
            refreshAfterNativeEvent();
          }
        },
        () => {
          if (isLive()) {
            if (active === operation) operation?.finish(false);
            notify();
          }
        }
      );
    }
  };
  listen(scope, "change", onChange, true);
  listen(scope, PRODUCT_SELECT, onProductSelect, true);
  listen(view, "pageshow", () => {
    notify();
    refreshAfterNativeEvent();
  });
  const Observer = view?.MutationObserver || globalThis.MutationObserver;
  const observer = Observer && scope ? new Observer(() => {
    if (!isLive()) {
      active?.finish(false);
      return;
    }
    notify();
  }) : null;
  observer?.observe(scope, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["data-product-id", "data-section-id", "checked", "value", "disabled", "aria-disabled"]
  });
  function blocked(control) {
    return !control || control.disabled || control.matches?.(":disabled") || control.getAttribute("aria-disabled") === "true" || control.dataset?.optionAvailable === "false" || !!control.dataset?.connectedProductUrl;
  }
  async function select({ size, variantId, scopeOptions, productId: requestedProductId } = {}) {
    active?.finish(false);
    if (!isLive() || requestedProductId != null && id2(requestedProductId) !== pid || typeof size !== "string" || !Array.isArray(scopeOptions) || scopeOptions.length !== names.length || scopeOptions.some((value, index) => index !== sizeOptionIndex && (typeof value !== "string" || !value))) return false;
    const before = readContext();
    if (!before.ready || !same(before.scopeOptions, scopeOptions.map((value, index) => index === sizeOptionIndex ? null : value))) return false;
    const expected = before.options.map((value, index) => index === sizeOptionIndex ? size : value);
    const candidates = catalog.filter((variant) => same(variant.options, expected));
    if (candidates.length !== 1 || candidates[0].available !== true || variantId != null && id2(variantId) !== id2(candidates[0].id)) return false;
    const targetId = id2(candidates[0].id);
    const source = sources();
    const name = names[sizeOptionIndex];
    const radios = values(source.picker?.querySelectorAll('input[type="radio"]')).filter((input) => input.dataset.optionName === name && input.value === size);
    const selects = values(source.picker?.querySelectorAll("select")).filter((control) => control.name === `options[${name}]`);
    let activate;
    if (radios.length) {
      if (radios.length !== 1 || blocked(radios[0])) return false;
      activate = () => radios[0].click();
    } else if (selects.length) {
      const options = values(selects[0].options).filter((option) => option.value === size);
      if (selects.length !== 1 || blocked(selects[0]) || options.length !== 1 || blocked(options[0])) return false;
      activate = () => {
        selects[0].value = size;
        const EventClass = view?.Event || Event;
        selects[0].dispatchEvent(new EventClass("change", { bubbles: true }));
      };
    } else if (!same(before.options, expected)) return false;
    if (before.variantId === targetId && same(before.options, expected)) return true;
    return new Promise((resolve) => {
      const started = Date.now();
      let timer = null;
      const operation = {
        nativePending: false,
        finish(success) {
          if (active !== operation) return;
          if (timer !== null) clearTimeout(timer);
          active = null;
          resolve(success);
        }
      };
      active = operation;
      const tick = () => {
        if (active !== operation) return;
        const context = readContext();
        if (!isLive() || !same(context.options, expected)) {
          operation.finish(false);
          return;
        }
        if (!operation.nativePending && context.ready && context.variantId === targetId) {
          operation.finish(true);
          notify();
          return;
        }
        if (Date.now() - started >= SELECT_CONFIRM_MS) {
          operation.finish(false);
          return;
        }
        timer = setTimeout(tick, POLL_MS);
      };
      try {
        ownDispatch = true;
        activate?.();
      } catch (_) {
        operation.finish(false);
      } finally {
        ownDispatch = false;
      }
      if (active === operation) tick();
    });
  }
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    active?.finish(false);
    if (refreshTimer !== null) clearTimeout(refreshTimer);
    refreshTimer = null;
    observer?.disconnect();
    for (const remove of listeners) remove();
  }
  return { readContext, select, destroy };
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-pdp-config.js
var CLOTHING_CATEGORIES = /* @__PURE__ */ new Set([
  "tank",
  "tshirt",
  "bottom",
  "camisole",
  "bratop",
  "longsleeve-top",
  "outer",
  "tshirt-maker",
  "dress"
]);
function numericMeasurement2(value) {
  if (typeof value === "number") return Number.isFinite(value) ? [value] : null;
  if (typeof value !== "string") return null;
  const match = /^(?:約\s*)?(\d+(?:\.\d+)?)(?:\s*[-‐‑‒–—―〜～~]\s*(\d+(?:\.\d+)?))?\s*(?:cm|㎝|センチ)?$/i.exec(value.trim());
  if (!match) return null;
  const numbers = match.slice(1).filter((part) => part !== void 0).map(Number);
  return numbers.every(Number.isFinite) && (numbers.length < 2 || numbers[0] <= numbers[1]) ? numbers : null;
}
function tableMatchesGuide(table, rules) {
  if (!table || !table.rows.length) return false;
  const headers = table.headers.slice(1), rows = table.rows.map((row) => row.label);
  if (new Set(headers).size !== headers.length || new Set(rows).size !== rows.length) return false;
  const labels = rules.sizes.map((size) => size.label);
  const columns = labels.every((label) => headers.includes(label));
  const vertical = labels.every((label) => rows.includes(label));
  if (columns === vertical) return false;
  return rules.sizes.every((size) => {
    const measurements = Object.entries(size.measurements || {});
    return measurements.length > 0 && measurements.every(([dimension, value]) => {
      const row = table.rows.find((item) => item.label === (columns ? dimension : size.label));
      const index = table.headers.indexOf(columns ? size.label : dimension);
      if (!row || index < 1) return false;
      const guide = numericMeasurement2(value), original = numericMeasurement2(row.values[index]);
      return guide !== null && original !== null && guide.length === original.length && guide.every((number, part) => number === original[part]);
    });
  });
}
function readPdpConfig(raw, document) {
  if (!raw || raw.schemaVersion !== "ayunel-body-choice-pdp-v1" || !raw.product || typeof raw.sectionId !== "string" || !raw.sectionId) throw new Error("PDP_CONFIG");
  const asset = (value2, extension) => {
    if (typeof value2 !== "string" || value2.length > 1024) throw new Error("PDP_ASSET");
    const url = new URL(value2, document.location.href);
    if (url.origin !== document.location.origin || !["http:", "https:"].includes(url.protocol) || url.username || url.password || url.hash || !url.pathname.endsWith(extension) || !(/^\/cdn\/shop\/t\/\d+\/assets\/[\w.-]+$/.test(url.pathname) || /^\/assets\/[\w.-]+$/.test(url.pathname))) throw new Error("PDP_ASSET");
    return url.href;
  };
  const value = raw.product;
  if (!Number.isInteger(value.variantCount) || value.variantCount < 1 || value.variantCount > 50 || !Array.isArray(value.variants) || value.variants.length !== value.variantCount || !Array.isArray(value.optionNames)) throw new Error("PDP_PRODUCT");
  const sizeOptions = value.optionNames.flatMap((name, index) => typeof name === "string" && ["size", "サイズ"].includes(name.toLowerCase().trim()) ? [index] : []);
  if (sizeOptions.length !== 1 || value.sizeOptionIndex !== sizeOptions[0]) throw new Error("PDP_PRODUCT");
  let sizeTable = null;
  if (typeof value.sizeTableHtml === "string" && value.sizeTableHtml.length <= 1e5) {
    const template = document.createElement("template");
    template.innerHTML = value.sizeTableHtml;
    template.content.querySelectorAll("script,style,iframe,object,embed,template").forEach((node) => node.remove());
    template.content.querySelectorAll("br").forEach((node) => node.replaceWith(document.createTextNode(" / ")));
    const table = template.content.querySelector("table");
    const rows = table ? [...table.rows].filter((row) => row.closest("table") === table).map((row) => [...row.cells].map((cell) => cell.textContent.replace(/\s+/g, " ").trim())) : [];
    if (rows.length >= 2 && rows[0].length >= 2 && rows[0].length <= 30 && rows.length <= 61 && rows.every((row) => row.length === rows[0].length && row.every((text2) => text2.length <= 256))) {
      sizeTable = { headers: rows[0], rows: rows.slice(1).map((row) => ({ label: row[0], values: row })) };
    }
  }
  const rules = value.rules;
  const overrides = value.overrides && {
    categories: value.overrides.categories,
    cells: Array.isArray(value.overrides.cells) ? value.overrides.cells.map((cell) => ({ h: cell.h, w: cell.w, size: cell.size })) : null
  };
  const product = {
    id: value.id,
    title: value.title,
    handle: value.handle,
    url: value.url,
    updatedAt: value.updatedAt,
    source: "registered_product_guide",
    rules,
    cupFit: value.cupFit || {},
    sizeTable,
    optionNames: value.optionNames,
    sizeOptionIndex: value.sizeOptionIndex,
    variants: value.variants,
    overrides
  };
  const asOf = typeof raw.asOf === "string" ? raw.asOf.replace(/([+-]\d{2})(\d{2})$/, "$1:$2") : raw.asOf;
  const checked = validateCatalog({
    schemaVersion: "ayunel-body-choice-catalog-v1",
    asOf,
    products: [product]
  });
  const accepted = checked.valid ? checked.catalog.products[0] : null;
  if (!accepted?.rules || !CLOTHING_CATEGORIES.has(accepted.rules.category) || !tableMatchesGuide(accepted.sizeTable, accepted.rules)) throw new Error("PDP_PRODUCT");
  const nativeSizes = new Set(accepted.variants.map((variant) => variant.options[accepted.sizeOptionIndex]));
  const guideSizes = new Set(accepted.rules.sizes.map((size) => size.label));
  if (nativeSizes.size !== guideSizes.size || [...nativeSizes].some((label) => !guideSizes.has(label)))
    throw new Error("PDP_PRODUCT");
  return {
    catalog: checked.catalog,
    productId: product.id,
    sectionId: raw.sectionId,
    optionNames: product.optionNames,
    sizeOptionIndex: product.sizeOptionIndex,
    variants: product.variants,
    cssUrl: asset(raw.cssUrl, ".css"),
    illustrationUrl: asset(raw.illustrationUrl, ".png")
  };
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-motion.js
function createDialogMotion(dialog) {
  const view = dialog.ownerDocument?.defaultView;
  let preference = null;
  try {
    preference = view?.matchMedia?.("(prefers-reduced-motion: reduce)") || null;
  } catch (_) {
  }
  let active = null;
  let destroyed = false;
  const schedule = (fn, ms) => (view?.setTimeout || setTimeout).call(view, fn, ms);
  const unschedule = (token) => (view?.clearTimeout || clearTimeout).call(view, token);
  function hiddenFrame() {
    let mobile = false;
    try {
      mobile = view?.matchMedia?.("(max-width: 749px)").matches === true;
    } catch (_) {
    }
    return { transform: mobile ? "translateY(24px) scale(1)" : "translateY(12px) scale(0.98)", opacity: 0 };
  }
  function currentFrame() {
    try {
      const presented = view?.getComputedStyle(dialog);
      const opacity = Number.parseFloat(presented?.opacity);
      return {
        transform: presented?.transform || "none",
        opacity: Number.isFinite(opacity) ? Math.min(1, Math.max(0, opacity)) : 1
      };
    } catch (_) {
      return { transform: "none", opacity: 1 };
    }
  }
  function backdropFrame() {
    let opacity = 1;
    try {
      const value = Number.parseFloat(view?.getComputedStyle(dialog, "::backdrop")?.opacity);
      if (Number.isFinite(value) && value >= 0 && value <= 1) opacity = value;
    } catch (_) {
    }
    dialog.style.setProperty("--pdp-backdrop-from", String(opacity));
  }
  function stop(operation) {
    if (operation.timer !== null) unschedule(operation.timer);
    operation.timer = null;
    try {
      operation.animation?.cancel();
    } catch (_) {
    }
  }
  function settle(operation) {
    if (active !== operation || destroyed) return;
    active = null;
    stop(operation);
    dialog.dataset.motion = operation.kind === "enter" ? "open" : "closing";
    operation.done?.();
  }
  function run(kind, done) {
    if (destroyed) {
      done?.();
      return;
    }
    const first = active || kind === "exit" ? currentFrame() : hiddenFrame();
    if (kind === "exit") backdropFrame();
    const previous = active;
    active = null;
    if (previous) stop(previous);
    dialog.dataset.motion = kind === "enter" ? "entering" : "closing";
    const operation = { kind, done, animation: null, timer: null };
    active = operation;
    if (preference?.matches || typeof dialog.animate !== "function") {
      settle(operation);
      return;
    }
    const duration = kind === "enter" ? 320 : 220;
    const last = kind === "enter" ? { transform: "translateY(0px) scale(1)", opacity: 1 } : hiddenFrame();
    try {
      operation.animation = dialog.animate([first, last], {
        duration,
        easing: kind === "enter" ? "cubic-bezier(.32,.72,0,1)" : "cubic-bezier(.55,.06,.68,.19)",
        fill: "both"
      });
      Promise.resolve(operation.animation.finished).then(() => settle(operation), () => settle(operation));
      operation.timer = schedule(() => settle(operation), duration + 100);
    } catch (_) {
      settle(operation);
    }
  }
  const preferenceChanged = () => {
    if (preference?.matches && active) settle(active);
  };
  if (preference?.addEventListener) preference.addEventListener("change", preferenceChanged);
  else preference?.addListener?.(preferenceChanged);
  function cancel() {
    const operation = active;
    active = null;
    if (operation) stop(operation);
    delete dialog.dataset.motion;
    dialog.style.removeProperty("--pdp-backdrop-from");
  }
  return {
    enter: (done) => run("enter", done),
    exit: (done) => run("exit", done),
    cancel,
    destroy() {
      if (destroyed) return;
      cancel();
      destroyed = true;
      if (preference?.removeEventListener) preference.removeEventListener("change", preferenceChanged);
      else preference?.removeListener?.(preferenceChanged);
    }
  };
}

// 08-themes/size-diagnosis-src/body-choice/body-choice-panel.html
var body_choice_panel_default = '<div class="ayunel-body-options">\n  <div class="ayunel-prototype-notice">確認用プレビュー・商品ページのサイズ選択やカートには反映されません。</div>\n  <main class="ayunel-page">\n    <header class="ayunel-intro">\n      <p class="ayunel-wordmark">AYUNEL</p>\n      <p class="ayunel-eyebrow">あなたのサイズ選びに</p>\n      <h1>この服のサイズを、<br>一緒に確かめましょう。</h1>\n      <p class="ayunel-lead">身長と体重から、サイズの目安をご案内します。</p>\n      <p class="ayunel-purpose">この機能では、入力内容を保存・送信しません。</p>\n    </header>\n    <ol class="ayunel-steps" aria-label="サイズ選びの手順">\n      <li aria-current="step" data-step-marker="1"><span class="ayunel-step-num">1</span><span class="ayunel-step-label">商品・身長・体重</span></li>\n      <li data-step-marker="2"><span class="ayunel-step-num">2</span><span class="ayunel-step-label">体型</span></li>\n      <li data-step-marker="3"><span class="ayunel-step-num">3</span><span class="ayunel-step-label">結果</span></li>\n    </ol>\n    <div id="ayunel-load-status" class="ayunel-message" role="status">商品サイズ表を読み込んでいます…</div>\n    <noscript><p class="ayunel-message">このプレビューでは JavaScript が必要です。商品ページのサイズ表をご確認ください。</p></noscript>\n    <form id="ayunel-size-form" novalidate hidden>\n      <section id="ayunel-step-1" class="ayunel-panel" aria-labelledby="ayunel-step-1-title">\n        <div class="ayunel-section-heading"><h2 id="ayunel-step-1-title" tabindex="-1">商品と身長・体重</h2><span class="ayunel-step-position">STEP 1 / 3</span></div>\n        <div class="ayunel-product-field">\n          <label class="ayunel-input-label" for="ayunel-product">サイズを確かめる商品</label>\n          <select id="ayunel-product" name="product" required aria-describedby="ayunel-product-note"><option value="">商品を選んでください</option></select>\n          <p id="ayunel-product-note" class="ayunel-detail">掲載商品の公開サイズ情報を使います。</p>\n        </div>\n        <div id="ayunel-product-info" class="ayunel-product-info" hidden></div>\n        <div id="ayunel-variant-scopes" class="ayunel-variant-scopes"></div>\n        <div class="ayunel-measure-inputs">\n          <div class="ayunel-field"><label class="ayunel-input-label" for="ayunel-height">身長</label><div class="ayunel-unit-input"><input id="ayunel-height" name="heightCm" type="number" inputmode="decimal" step="0.1" min="130" max="200" autocomplete="off" required aria-describedby="ayunel-number-note"><span>cm</span></div></div>\n          <div class="ayunel-field"><label class="ayunel-input-label" for="ayunel-weight">体重</label><div class="ayunel-unit-input"><input id="ayunel-weight" name="weightKg" type="number" inputmode="decimal" step="0.1" min="30" max="120" autocomplete="off" required aria-describedby="ayunel-number-note"><span>kg</span></div></div>\n        </div>\n        <p id="ayunel-number-note" class="ayunel-detail">身長 130〜200cm、体重 30〜120kg で入力できます。</p>\n        <p id="ayunel-product-change-note" class="ayunel-detail" hidden>商品を変えたため、結果とブラサイズをリセットしました。</p>\n        <p id="ayunel-input-error" class="ayunel-message" role="alert" hidden></p>\n        <div class="ayunel-form-actions"><button id="ayunel-next" type="button" class="ayunel-button ayunel-button-primary">次へ：体型（任意） <span aria-hidden="true">→</span></button></div>\n      </section>\n      <section id="ayunel-step-2" class="ayunel-panel" aria-labelledby="ayunel-step-2-title" hidden>\n        <button id="ayunel-back-input" type="button" class="ayunel-button ayunel-button-text ayunel-step-back">商品・身長・体重に戻る</button>\n        <div class="ayunel-section-heading"><h2 id="ayunel-step-2-title" tabindex="-1">近い体型はありますか</h2><span class="ayunel-optional">任意</span></div>\n        <p class="ayunel-illustration-note">わからなければ飛ばせます。体型は、実寸を確かめたい箇所のご案内に使います（サイズは変わりません）。</p>\n        <p id="ayunel-body-relevance-note" class="ayunel-detail"></p>\n        <div id="ayunel-groups" class="ayunel-groups"></div>\n        <fieldset id="ayunel-bra-fieldset" class="ayunel-bra-fieldset" hidden>\n          <legend>普段お使いのブラサイズ <span class="ayunel-optional">任意</span></legend>\n          <label class="ayunel-input-label" for="ayunel-bra-size">アンダーとカップを選んでください</label>\n          <select id="ayunel-bra-size" name="braSize"><option value="">わからない・選ばない</option></select>\n          <p class="ayunel-detail">この商品の対応表と照らし合わせるため、サイズが変わることがあります。</p>\n        </fieldset>\n        <div class="ayunel-form-actions ayunel-body-actions"><p id="ayunel-selection-count" class="ayunel-selection-count" aria-live="polite">選択：0 / 4 項目</p><button id="ayunel-recommend" type="submit" class="ayunel-button ayunel-button-primary">サイズの目安を見る <span aria-hidden="true">→</span></button></div>\n      </section>\n      <section id="ayunel-step-3" class="ayunel-panel ayunel-results" aria-labelledby="ayunel-result-title" hidden>\n        <div class="ayunel-section-heading"><h2 id="ayunel-result-title" tabindex="-1">結果</h2><button id="ayunel-edit" type="button" class="ayunel-button ayunel-button-outline">入力を見直す</button></div>\n        <p id="ayunel-result-product" class="ayunel-result-product"></p>\n        <div class="ayunel-verdict"><div id="ayunel-result-overview" class="ayunel-result-overview"></div></div>\n        <section id="ayunel-chart-section" class="ayunel-result-section ayunel-decide" aria-labelledby="ayunel-chart-title">\n          <h3 id="ayunel-chart-title">実寸を見て選ぶ</h3>\n          <p id="ayunel-inventory-note" class="ayunel-detail"></p>\n          <div id="ayunel-size-tabs" class="ayunel-size-tabs" role="group" aria-label="比較するサイズ"></div>\n          <div id="ayunel-comparison" class="ayunel-comparison" aria-live="polite"></div>\n          <div class="ayunel-form-actions"><button id="ayunel-local-select" type="button" class="ayunel-button ayunel-button-primary" disabled>比較するサイズを選んでください</button></div>\n          <p id="ayunel-local-selection" class="ayunel-local-note" aria-live="polite">このプレビュー内の選択です。商品ページやカートには反映されません。</p>\n        </section>\n        <div class="ayunel-more">\n          <section class="ayunel-result-section" aria-labelledby="ayunel-reasons-title"><details class="ayunel-disclosure"><summary><h3 id="ayunel-reasons-title">理由をくわしく</h3></summary><div class="ayunel-disclosure-content"><div id="ayunel-reasons" class="ayunel-reasons"></div></div></details></section>\n          <section id="ayunel-second-section" class="ayunel-result-section" aria-labelledby="ayunel-second-title" hidden><details class="ayunel-disclosure"><summary><h3 id="ayunel-second-title">もうひとつのサイズと比べる</h3></summary><div class="ayunel-disclosure-content"><div id="ayunel-second-comparison" class="ayunel-chart"></div></div></details></section>\n          <section id="ayunel-body-check-section" class="ayunel-result-section" aria-labelledby="ayunel-body-check-title"><details class="ayunel-disclosure"><summary><h3 id="ayunel-body-check-title">体型から見たい箇所</h3></summary><div class="ayunel-disclosure-content"><p class="ayunel-detail">体型の選択は、寸法を確認する箇所のご案内に使っています。</p><div id="ayunel-body-checks" class="ayunel-checks"></div></div></details></section>\n          <section class="ayunel-result-section" aria-labelledby="ayunel-full-chart-title"><details class="ayunel-disclosure"><summary><h3 id="ayunel-full-chart-title">全サイズの表</h3></summary><div class="ayunel-disclosure-content"><p class="ayunel-detail">服の実寸は、体のサイズとは異なります。身長・体重の目安とも別の情報です。</p><p class="ayunel-detail">表は横にスクロールできます。</p><div id="ayunel-chart" class="ayunel-chart" tabindex="0" role="region" aria-label="商品ページの全サイズ表"></div></div></details></section>\n          <section class="ayunel-result-section" aria-labelledby="ayunel-input-summary-title"><details class="ayunel-disclosure"><summary><h3 id="ayunel-input-summary-title">今回の入力</h3></summary><div class="ayunel-disclosure-content"><dl id="ayunel-input-summary" class="ayunel-summary"></dl></div></details></section>\n          <section class="ayunel-result-section" aria-labelledby="ayunel-source-title"><details class="ayunel-disclosure"><summary><h3 id="ayunel-source-title">この目安について</h3></summary><div class="ayunel-disclosure-content"><div id="ayunel-source" class="ayunel-detail"></div></div></details></section>\n        </div>\n        <div class="ayunel-results-foot"><p class="ayunel-confirmation-footnote">着心地は素材やお好みでも変わります。実寸もあわせてご覧ください。</p><button id="ayunel-edit-body" type="button" class="ayunel-button ayunel-button-text">体型の選択を見直す</button></div>\n      </section>\n    </form>\n    <footer class="ayunel-footer">AYUNEL · サイズ選びの確認用プレビュー</footer>\n  </main>\n</div>\n';

// 08-themes/size-diagnosis-src/body-choice/body-choice-pdp.js
var SCOPE2 = "product-card, dialog, .shopify-section";
var sameScope = (left, right) => Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((value, index) => value === right[index]);
var scrollLocks = /* @__PURE__ */ new WeakMap();
function lockPageScroll(document) {
  const html = document.documentElement;
  const body = document.body;
  if (!html?.style) return () => {
  };
  let record2 = scrollLocks.get(document);
  if (!record2) {
    const changed = [];
    const write = (element, property, value) => {
      const style = element.style;
      const previous = { value: style.getPropertyValue(property), priority: style.getPropertyPriority(property) };
      if (previous.value === value) return;
      style.setProperty(property, value, "important");
      changed.push({ style, property, value, previous });
    };
    const gap = Math.max(0, (document.defaultView?.innerWidth || 0) - html.clientWidth);
    if (body?.style && gap > 0) {
      const padding = Number.parseFloat(document.defaultView?.getComputedStyle(body).paddingRight) || 0;
      write(body, "padding-right", `${padding + gap}px`);
    }
    write(html, "overflow", "hidden");
    record2 = { owners: 0, changed };
    scrollLocks.set(document, record2);
  }
  record2.owners += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--record2.owners > 0) return;
    scrollLocks.delete(document);
    for (const { style, property, value, previous } of record2.changed) {
      if (style.getPropertyValue(property) !== value || style.getPropertyPriority(property) !== "important") continue;
      if (previous.value) style.setProperty(property, previous.value, previous.priority);
      else style.removeProperty(property);
    }
  };
}
var _generation, _session, _controller, _bridge, _panel, _dialog, _config, _button, _legacy, _scopeOptions, _ready, _opening, _returnFocus, _cancelCss, _releaseScroll, _motion, _panelRoot, _closeWork, _ownCloseEvents, _nativeRevision, _BodyChoicePdp_instances, listen_fn, initialize_fn, live_fn, createBridge_fn, waitForNative_fn, open_fn, select_fn, clearStatus_fn, invalidateSession_fn, finishClosedState_fn, disposeSession_fn, close_fn, hideLegacy_fn, restoreLegacy_fn;
var BodyChoicePdp = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _BodyChoicePdp_instances);
    __privateAdd(this, _generation, 0);
    __privateAdd(this, _session, 0);
    __privateAdd(this, _controller);
    __privateAdd(this, _bridge);
    __privateAdd(this, _panel);
    __privateAdd(this, _dialog);
    __privateAdd(this, _config);
    __privateAdd(this, _button);
    __privateAdd(this, _legacy, []);
    __privateAdd(this, _scopeOptions, []);
    __privateAdd(this, _ready, false);
    __privateAdd(this, _opening, false);
    __privateAdd(this, _returnFocus);
    __privateAdd(this, _cancelCss);
    __privateAdd(this, _releaseScroll);
    __privateAdd(this, _motion);
    __privateAdd(this, _panelRoot);
    __privateAdd(this, _closeWork);
    __privateAdd(this, _ownCloseEvents, /* @__PURE__ */ new WeakMap());
    __privateAdd(this, _nativeRevision, 0);
  }
  connectedCallback() {
    __privateMethod(this, _BodyChoicePdp_instances, initialize_fn).call(this);
  }
  disconnectedCallback() {
    var _a;
    __privateSet(this, _generation, __privateGet(this, _generation) + 1);
    __privateSet(this, _session, __privateGet(this, _session) + 1);
    __privateGet(this, _controller)?.abort();
    (_a = __privateGet(this, _cancelCss)) == null ? void 0 : _a.call(this);
    __privateSet(this, _cancelCss, null);
    __privateMethod(this, _BodyChoicePdp_instances, disposeSession_fn).call(this);
    __privateGet(this, _motion)?.destroy();
    __privateSet(this, _motion, null);
    __privateMethod(this, _BodyChoicePdp_instances, clearStatus_fn).call(this);
    __privateGet(this, _panel)?.destroy();
    __privateSet(this, _panel, null);
    __privateSet(this, _ready, false);
    __privateMethod(this, _BodyChoicePdp_instances, restoreLegacy_fn).call(this);
    this.hidden = true;
    this.removeAttribute("data-body-choice-ready");
  }
};
_generation = new WeakMap();
_session = new WeakMap();
_controller = new WeakMap();
_bridge = new WeakMap();
_panel = new WeakMap();
_dialog = new WeakMap();
_config = new WeakMap();
_button = new WeakMap();
_legacy = new WeakMap();
_scopeOptions = new WeakMap();
_ready = new WeakMap();
_opening = new WeakMap();
_returnFocus = new WeakMap();
_cancelCss = new WeakMap();
_releaseScroll = new WeakMap();
_motion = new WeakMap();
_panelRoot = new WeakMap();
_closeWork = new WeakMap();
_ownCloseEvents = new WeakMap();
_nativeRevision = new WeakMap();
_BodyChoicePdp_instances = new WeakSet();
listen_fn = function(node, type, listener, options = {}) {
  node.addEventListener(type, listener, { ...options, signal: __privateGet(this, _controller).signal });
};
initialize_fn = async function() {
  var _a;
  const generation = ++__privateWrapper(this, _generation)._;
  __privateSet(this, _ready, false);
  this.hidden = true;
  __privateGet(this, _controller)?.abort();
  __privateSet(this, _controller, new AbortController());
  try {
    const script = this.querySelector('script[type="application/json"][data-body-choice-config]');
    __privateSet(this, _button, this.querySelector("[data-body-choice-open]"));
    if (!script || !__privateGet(this, _button) || script.textContent.length > 262144) throw new Error("Missing PDP body-choice configuration");
    __privateSet(this, _config, readPdpConfig(JSON.parse(script.textContent), this.ownerDocument));
    if (String(__privateGet(this, _config).productId) !== this.dataset.productId || __privateGet(this, _config).sectionId !== this.dataset.sectionId) throw new Error("PDP configuration owner mismatch");
    const shadow = this.shadowRoot || this.attachShadow({ mode: "open" });
    shadow.innerHTML = `<slot></slot><p class="pdp-status" role="status" hidden></p><dialog class="pdp-dialog" aria-label="サイズ選びの目安">
        <div class="pdp-dialog-header"><h2 class="pdp-dialog-title">サイズ選びの目安</h2>
          <button class="pdp-close" type="button" data-body-choice-close aria-label="サイズ選びの画面を閉じる"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M6 6 18 18M18 6 6 18"/></svg></button></div>
        <div class="pdp-panel">${body_choice_panel_default}</div></dialog>`;
    __privateSet(this, _dialog, shadow.querySelector(".pdp-dialog"));
    if (typeof __privateGet(this, _dialog)?.showModal !== "function") throw new Error("Native dialog unavailable");
    const panelRoot = shadow.querySelector(".pdp-panel");
    __privateSet(this, _panelRoot, panelRoot);
    __privateSet(this, _motion, createDialogMotion(__privateGet(this, _dialog)));
    panelRoot.querySelector(".ayunel-prototype-notice")?.remove();
    panelRoot.querySelector(".ayunel-footer")?.remove();
    const css = this.ownerDocument.createElement("link");
    css.rel = "stylesheet";
    const cssReady = new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        css.removeEventListener("load", loaded);
        css.removeEventListener("error", failed);
        if (__privateGet(this, _cancelCss) === cancel) __privateSet(this, _cancelCss, null);
        error ? reject(error) : resolve();
      };
      const loaded = () => finish();
      const failed = () => finish(new Error("Body-choice stylesheet unavailable"));
      const cancel = () => finish(new Error("Body-choice disconnected"));
      const timeout = setTimeout(() => finish(new Error("Body-choice stylesheet timed out")), 5e3);
      __privateSet(this, _cancelCss, cancel);
      css.addEventListener("load", loaded, { once: true });
      css.addEventListener("error", failed, { once: true });
      css.href = __privateGet(this, _config).cssUrl;
      shadow.prepend(css);
    });
    __privateSet(this, _bridge, __privateMethod(this, _BodyChoicePdp_instances, createBridge_fn).call(this));
    const [context] = await Promise.all([__privateMethod(this, _BodyChoicePdp_instances, waitForNative_fn).call(this, generation, __privateGet(this, _session)), cssReady]);
    if (!__privateMethod(this, _BodyChoicePdp_instances, live_fn).call(this, generation)) return;
    if (!context?.ready) throw new Error("Native product context unavailable");
    __privateSet(this, _scopeOptions, [...context.scopeOptions]);
    __privateSet(this, _panel, mountBodyChoice(panelRoot, {
      embedded: true,
      catalog: __privateGet(this, _config).catalog,
      productId: __privateGet(this, _config).productId,
      scopeOptions: __privateGet(this, _scopeOptions),
      illustrationUrl: __privateGet(this, _config).illustrationUrl,
      onSelect: (payload) => __privateMethod(this, _BodyChoicePdp_instances, select_fn).call(this, payload)
    }));
    if (!__privateGet(this, _panel) || panelRoot.querySelector('[id="ayunel-size-form"]')?.hidden !== false) {
      throw new Error("Body-choice product data unavailable");
    }
    __privateGet(this, _bridge).destroy();
    __privateSet(this, _bridge, null);
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, __privateGet(this, _button), "click", (event) => {
      event.preventDefault();
      __privateMethod(this, _BodyChoicePdp_instances, open_fn).call(this);
    });
    const nativeScope = this.closest(SCOPE2);
    const clearOnNativeSelection = (event) => {
      const picker = event.target?.closest?.("variant-picker, swatches-variant-picker-component");
      if (picker?.closest(SCOPE2) === nativeScope && String(picker.dataset.productId) === String(__privateGet(this, _config).productId) && picker.dataset.sectionId === __privateGet(this, _config).sectionId) {
        __privateSet(this, _nativeRevision, __privateGet(this, _nativeRevision) + 1);
        __privateMethod(this, _BodyChoicePdp_instances, clearStatus_fn).call(this);
      }
    };
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, nativeScope, "change", clearOnNativeSelection, { capture: true });
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, nativeScope, "shopify:product:select", clearOnNativeSelection, { capture: true });
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, shadow.querySelector("[data-body-choice-close]"), "click", () => __privateMethod(this, _BodyChoicePdp_instances, close_fn).call(this));
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, __privateGet(this, _dialog), "cancel", (event) => {
      event.preventDefault();
      __privateMethod(this, _BodyChoicePdp_instances, close_fn).call(this);
    });
    const boundDialog = __privateGet(this, _dialog);
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, boundDialog, "close", () => {
      const ownEvents = __privateGet(this, _ownCloseEvents).get(boundDialog) || 0;
      if (ownEvents) {
        __privateGet(this, _ownCloseEvents).set(boundDialog, ownEvents - 1);
        return;
      }
      if (__privateGet(this, _dialog) !== boundDialog) return;
      __privateMethod(this, _BodyChoicePdp_instances, disposeSession_fn).call(this, true);
    });
    __privateMethod(this, _BodyChoicePdp_instances, listen_fn).call(this, __privateGet(this, _dialog), "click", (event) => {
      if (event.target !== __privateGet(this, _dialog)) return;
      const rect = __privateGet(this, _dialog).getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) __privateMethod(this, _BodyChoicePdp_instances, close_fn).call(this);
    });
    __privateGet(this, _button).setAttribute("aria-haspopup", "dialog");
    __privateGet(this, _button).setAttribute("aria-expanded", "false");
    __privateMethod(this, _BodyChoicePdp_instances, hideLegacy_fn).call(this);
    __privateSet(this, _ready, true);
    this.setAttribute("data-body-choice-ready", "true");
    this.hidden = false;
  } catch (_) {
    if (!__privateMethod(this, _BodyChoicePdp_instances, live_fn).call(this, generation)) return;
    __privateGet(this, _controller)?.abort();
    (_a = __privateGet(this, _cancelCss)) == null ? void 0 : _a.call(this);
    __privateSet(this, _cancelCss, null);
    __privateMethod(this, _BodyChoicePdp_instances, disposeSession_fn).call(this);
    __privateGet(this, _motion)?.destroy();
    __privateSet(this, _motion, null);
    __privateGet(this, _panel)?.destroy();
    __privateSet(this, _panel, null);
    __privateMethod(this, _BodyChoicePdp_instances, restoreLegacy_fn).call(this);
    __privateSet(this, _ready, false);
    this.hidden = true;
    this.removeAttribute("data-body-choice-ready");
  }
};
live_fn = function(generation) {
  return generation === __privateGet(this, _generation) && this.isConnected;
};
createBridge_fn = function() {
  const bridge = createNativeBridge(this, {
    ...__privateGet(this, _config),
    onContextChange: (context) => {
      if (__privateGet(this, _bridge) !== bridge || !__privateGet(this, _ready) || !__privateGet(this, _dialog)?.open || !context.ready || sameScope(context.scopeOptions, __privateGet(this, _scopeOptions))) return;
      __privateSet(this, _scopeOptions, [...context.scopeOptions]);
      __privateGet(this, _panel)?.setContext({ productId: __privateGet(this, _config).productId, scopeOptions: __privateGet(this, _scopeOptions) });
    }
  });
  return bridge;
};
waitForNative_fn = async function(generation, session, timeoutMs = 2e3) {
  const started = Date.now();
  while (__privateMethod(this, _BodyChoicePdp_instances, live_fn).call(this, generation) && session === __privateGet(this, _session) && __privateGet(this, _bridge)) {
    const context = __privateGet(this, _bridge).readContext();
    if (context.ready) return context;
    if (Date.now() - started >= timeoutMs) return null;
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  return null;
};
open_fn = async function() {
  if (!__privateGet(this, _ready) || __privateGet(this, _opening)) return;
  if (__privateGet(this, _closeWork)) __privateMethod(this, _BodyChoicePdp_instances, disposeSession_fn).call(this);
  if (__privateGet(this, _dialog).open) return;
  __privateMethod(this, _BodyChoicePdp_instances, clearStatus_fn).call(this);
  const session = ++__privateWrapper(this, _session)._;
  const generation = __privateGet(this, _generation);
  __privateSet(this, _opening, true);
  __privateGet(this, _button).setAttribute("aria-busy", "true");
  __privateSet(this, _returnFocus, this.ownerDocument.activeElement || __privateGet(this, _button));
  __privateGet(this, _bridge)?.destroy();
  __privateSet(this, _bridge, __privateMethod(this, _BodyChoicePdp_instances, createBridge_fn).call(this));
  __privateGet(this, _panel).reset();
  const context = await __privateMethod(this, _BodyChoicePdp_instances, waitForNative_fn).call(this, generation, session, 1e4);
  if (!__privateMethod(this, _BodyChoicePdp_instances, live_fn).call(this, generation) || session !== __privateGet(this, _session)) return;
  __privateSet(this, _opening, false);
  __privateGet(this, _button).removeAttribute("aria-busy");
  if (!context?.ready) {
    __privateMethod(this, _BodyChoicePdp_instances, disposeSession_fn).call(this);
    const status = this.shadowRoot.querySelector(".pdp-status");
    status.textContent = "商品ページで色・サイズを選んでから、もう一度お試しください。";
    status.hidden = false;
    __privateGet(this, _button).focus({ preventScroll: true });
    return;
  }
  __privateMethod(this, _BodyChoicePdp_instances, clearStatus_fn).call(this);
  __privateSet(this, _scopeOptions, [...context.scopeOptions]);
  __privateGet(this, _panel).setContext({
    catalog: __privateGet(this, _config).catalog,
    productId: __privateGet(this, _config).productId,
    scopeOptions: __privateGet(this, _scopeOptions)
  });
  try {
    __privateGet(this, _dialog).showModal();
    __privateSet(this, _releaseScroll, lockPageScroll(this.ownerDocument));
    __privateGet(this, _button).setAttribute("aria-expanded", "true");
    __privateGet(this, _motion).enter();
    this.shadowRoot.querySelector('[id="ayunel-height"]')?.focus({ preventScroll: true });
  } catch (_) {
    __privateMethod(this, _BodyChoicePdp_instances, disposeSession_fn).call(this, true);
    __privateMethod(this, _BodyChoicePdp_instances, restoreLegacy_fn).call(this);
    __privateSet(this, _ready, false);
    this.hidden = true;
    this.removeAttribute("data-body-choice-ready");
  }
};
select_fn = async function(payload) {
  const session = __privateGet(this, _session);
  const generation = __privateGet(this, _generation);
  if (!__privateGet(this, _ready) || __privateGet(this, _closeWork) || !__privateGet(this, _dialog).open || !__privateGet(this, _bridge) || String(payload.productId) !== String(__privateGet(this, _config).productId)) return false;
  const bridge = __privateGet(this, _bridge);
  const confirmed = await bridge.select(payload);
  if (!confirmed || !__privateMethod(this, _BodyChoicePdp_instances, live_fn).call(this, generation) || session !== __privateGet(this, _session) || bridge !== __privateGet(this, _bridge) || !__privateGet(this, _dialog).open) return false;
  const revision = __privateGet(this, _nativeRevision);
  const closedSession = __privateGet(this, _session) + 1;
  const closed = await __privateMethod(this, _BodyChoicePdp_instances, close_fn).call(this);
  if (!closed || !__privateMethod(this, _BodyChoicePdp_instances, live_fn).call(this, generation) || __privateGet(this, _session) !== closedSession || __privateGet(this, _dialog).open || revision !== __privateGet(this, _nativeRevision)) return false;
  const status = this.shadowRoot.querySelector(".pdp-status");
  status.textContent = `${payload.size}サイズを選択しました。カートにはまだ入っていません。`;
  status.hidden = false;
  return true;
};
clearStatus_fn = function() {
  const status = this.shadowRoot?.querySelector(".pdp-status");
  if (!status) return;
  status.textContent = "";
  status.hidden = true;
};
invalidateSession_fn = function() {
  __privateSet(this, _session, __privateGet(this, _session) + 1);
  __privateGet(this, _bridge)?.destroy();
  __privateSet(this, _bridge, null);
  __privateSet(this, _opening, false);
  __privateGet(this, _button)?.removeAttribute("aria-busy");
  __privateGet(this, _button)?.setAttribute("aria-expanded", "false");
};
finishClosedState_fn = function(restoreFocus) {
  var _a;
  const dialog = __privateGet(this, _dialog);
  if (dialog?.open) {
    const pending = __privateGet(this, _ownCloseEvents).get(dialog) || 0;
    __privateGet(this, _ownCloseEvents).set(dialog, pending + 1);
    try {
      dialog.close();
    } catch (_) {
      __privateGet(this, _ownCloseEvents).set(dialog, pending);
    }
  }
  __privateGet(this, _panel)?.reset();
  if (__privateGet(this, _panelRoot)) __privateGet(this, _panelRoot).inert = false;
  (_a = __privateGet(this, _releaseScroll)) == null ? void 0 : _a.call(this);
  __privateSet(this, _releaseScroll, null);
  const focus = __privateGet(this, _returnFocus)?.isConnected ? __privateGet(this, _returnFocus) : __privateGet(this, _button);
  __privateSet(this, _returnFocus, null);
  if (restoreFocus && this.isConnected) focus?.focus?.({ preventScroll: true });
};
disposeSession_fn = function(restoreFocus = false) {
  __privateMethod(this, _BodyChoicePdp_instances, invalidateSession_fn).call(this);
  const previous = __privateGet(this, _closeWork);
  __privateSet(this, _closeWork, null);
  __privateGet(this, _motion)?.cancel();
  previous?.resolve(false);
  __privateMethod(this, _BodyChoicePdp_instances, finishClosedState_fn).call(this, restoreFocus);
};
close_fn = function() {
  if (__privateGet(this, _closeWork)) return __privateGet(this, _closeWork).promise;
  if (!__privateGet(this, _dialog)?.open && !__privateGet(this, _opening) && !__privateGet(this, _bridge)) return Promise.resolve(false);
  __privateMethod(this, _BodyChoicePdp_instances, invalidateSession_fn).call(this);
  const focused = this.shadowRoot?.activeElement || this.ownerDocument.activeElement;
  if (__privateGet(this, _panelRoot)?.contains(focused)) {
    this.shadowRoot.querySelector("[data-body-choice-close]")?.focus({ preventScroll: true });
  }
  if (__privateGet(this, _panelRoot)) __privateGet(this, _panelRoot).inert = true;
  let resolve;
  const promise = new Promise((done) => {
    resolve = done;
  });
  const operation = { resolve, promise };
  __privateSet(this, _closeWork, operation);
  const complete = () => {
    if (__privateGet(this, _closeWork) !== operation) return;
    __privateSet(this, _closeWork, null);
    __privateGet(this, _motion)?.cancel();
    __privateMethod(this, _BodyChoicePdp_instances, finishClosedState_fn).call(this, true);
    resolve(true);
  };
  if (__privateGet(this, _dialog)?.open && __privateGet(this, _motion)) __privateGet(this, _motion).exit(complete);
  else complete();
  return promise;
};
hideLegacy_fn = function() {
  const scope = this.closest(SCOPE2);
  const pid = String(__privateGet(this, _config).productId);
  const widgets = Array.from(scope?.querySelectorAll("ayunel-size-diagnosis") || []).filter((widget) => widget.closest(SCOPE2) === scope && String(widget.dataset.productId) === pid);
  for (const widget of widgets) {
    for (const trigger of widget.querySelectorAll('[data-ref="trigger"], [data-action="open"]')) {
      __privateGet(this, _legacy).push({
        node: trigger,
        hidden: trigger.hidden,
        display: trigger.style.getPropertyValue("display"),
        priority: trigger.style.getPropertyPriority("display")
      });
      trigger.hidden = true;
      trigger.style.setProperty("display", "none", "important");
    }
  }
};
restoreLegacy_fn = function() {
  for (const previous of __privateGet(this, _legacy)) {
    previous.node.hidden = previous.hidden;
    if (previous.display) previous.node.style.setProperty("display", previous.display, previous.priority);
    else previous.node.style.removeProperty("display");
  }
  __privateSet(this, _legacy, []);
};
if (!customElements.get("ayunel-body-choice")) customElements.define("ayunel-body-choice", BodyChoicePdp);
export {
  BodyChoicePdp
};
